import React, { useCallback, useContext, useEffect, useMemo, useRef, useState, lazy } from 'react';
import { useHistory } from 'react-router-dom';
import { toast } from '../../helpers/toast';
import { isCorrelatoMedicine } from '../../helpers/medicineEditionUtils';
import { extractPatientAge } from '../../helpers/patientAge';
import PrescriptionLayout from './PrescriptionLayout';
import { PrescriberConfiguration } from '../../shared/prescriber';
import { Tracker } from '../../shared/tracker/Tracker';
import Search from '../../components/search/Search';
import { OpenModelsButton } from '../my-central/Models/OpenModelsButton';
import { SaveAsModelButton } from '../my-central/Models/SaveAsModelButton';
import { logger } from '../../helpers/logger';

import {
  BENEFIT_TYPE,
  getPbmBenefit,
  getPspBenefit,
  getWhatsappPbmBenefit,
  ManualMedicine,
  Medicine,
  PspProgramData,
  PT_PRESCRIPTION_TYPE,
} from '../../interfaces/medicine';
import { Exam } from '../../interfaces/exam';
import { sendCancelPostMessage } from '../../helpers/window';
import { MedicineService } from '../../components/search/medicineService';
import { fetchDoctorRqes } from '../../shared/bannerApi';
import { get, isEmpty, isEqual } from 'lodash';
import { PrescriptionService } from './service';
import { allExamsHaveSpecificIcd } from './helpers';

import { MedicalReferral } from '../../interfaces/MedicalReferral';
import { DialogWrappedComponentProps, FeedbackContext } from '../../providers/FeedbackProvider';
import { DateFromJSDate } from '../../helpers/Date';
import { Box } from '../../components/UI-CHAKRA/Box';
import JustifyInteractionsDialog from './JustifyInteractionsDialog/JustifyInteractionsDialog';
import { Divider } from '../../components/UI-CHAKRA/Divider';
import { useIsMobile } from '../../helpers/useIsMobile';
import { CancelPrescription } from '../../components/CancelPrescription';
import { Text } from '../../components/UI-CHAKRA/Text';
import {
  MedicalCertificate,
  MedicalCertificateDTO,
  MEDICAL_CERTIFICATE_TYPES,
} from '../../interfaces/medicalCertificate';
import { CanceledPrescription } from '../../components/CanceledPrescription';
import { isRunningInIframe } from '../../helpers/isRunningInIframe';

import { MedicalReport, MedicalReportDTO } from '../../interfaces/medicalReport';
import { isProfessionalIssuerDoctor } from '../../helpers/string';
import { ExamService } from '../../components/search/examService';
import { isMobileApp } from '../../helpers/isMobileApp';
import { Switch } from '../../components/UI-CHAKRA/Switch';
import { FormControl } from '../../components/UI-CHAKRA/FormControl';
import { FormLabel } from '../../components/UI-CHAKRA/FormLabel';
import { useDrawerStore } from '../../stores/drawerStore';
import { TEST_FLOW, usePrescriptionStore } from '../../stores/prescriptionStore';
import { resolveGenericLabInfo } from '../../stores/helpers/genericLabPreference';
import { trackGenericLabEvent as trackGenericLabEventHelper } from '../../stores/helpers/genericLabEvent';
import { useSearchTypeStore } from '../../stores/searchTypeStore';
import { useInteractionStore } from '../../stores/interactionStore';
import { usePathStore } from '../../stores/pathStore';
import { useSmrStore } from '../../stores/smrStore';
import { SEARCH_TYPE } from '../../components/search/SearchTypeEnum';
import { useApacBpaStore } from '../../stores/apacBpaStore';
import { PRESCRIPTION_STATUS } from '../../interfaces/prescription';
import { PrescriptionContext } from '../../providers/PrescriptionProvider';
import { InitialCertificateFlow } from './InitialCertificateFlow/InitialCertificateFlow';
import { TrackerEventService } from './trackerEventService';
import { saveConcomitanceOrigin, saveStructuredPosology, saveSelectedLaboratory } from '../../shared/amplitude/amplitudeEventStorage';
import { amplitudeModule } from '../../shared/amplitude';
import withSuspense from '../../components/withLazyComponent';
import { Anchor } from '../../components/notifications/Anchor';
import { ANCHORS, ANCHOR_POS } from '../../components/notifications/notifications';
import PrescriptionContent from './PrescriptionContent';
import { Icd } from '../my-central/patientService';
import { useExamStore } from '../../stores/examStore';
import { Modal, ModalContent, ModalOverlay } from '@chakra-ui/react';
import { SIDEBAR_WIDTH, useMyCentralStore } from '../../stores/myCentralStore';
import { HEADER_HEIGHT, HEADER_HEIGHT_MOBILE } from '../../contants/size';
import PrescriptionExamContent from './PrescriptionExamContent/PrescriptionExamContent';
import { EXAM_PERIOD_OPTIONS } from '../../components/helpers';
import AudioModal from '../../components/AudioModal';
import { IncompletePrescriptionsModal } from '../../components/AudioModal/components/IncompletePrescriptionsModal';
import PrescriptionFormulaContent from './PrescriptionFormulaContent/PrescriptionFormulaContent';
import { Formula } from '../../interfaces/formula';
import { isAndroid } from '../../helpers/isAndroid';
import PspSidebar from '../../components/PspSidebar';
import PspModal from '../../components/psp/PspModal';
import PbmSidebar from '../../components/PbmSidebar';
import PbmModal from '../../components/pbm/PbmModal';
import { PbmService } from '../../components/pbm/pbmService';
import { shouldShowPbmRescue, shouldShowWhatsappPbmRescue } from '../../components/pbm/pbmHelpers';
import WhatsappPbmSidebar from '../../components/WhatsappPbmSidebar';
import { WHATSAPP_PBM_PROGRAM_TYPE } from '../../components/WhatsappPbmSidebar/constants';
import { MedicineForEdition } from '../../components/search/EditMedicine/BaseEditMedicine';
import { APP_VIEWPORT_HEIGHT, appViewportHeight } from '../../helpers/embeddedViewport';

const EditManualMedicine = withSuspense(lazy(() => import('../../components/search/EditManualMedicine')));
const EditPrescriptionObservation = withSuspense(lazy(() => import('./EditPrescriptionObservation')));
const EditMedicine = withSuspense(lazy(() => import('../../components/search/EditMedicine/EditMedicine')));
const EditMedicalCertificate = withSuspense(lazy(() => import('../medical-certificate/EditMedicalCertificate')));
const EditMedicalReferral = withSuspense(lazy(() => import('../../components/search/EditMedicalReferral')));
const EditMedicalReport = withSuspense(lazy(() => import('../../components/search/EditMedicalReport')));

enum MEDICAL_CERTIFICATE_MODE {
  NONE = 0,
  EDITION = 1,
  ADDITION = 2,
}

enum MEDICAL_REPORT_MODE {
  NONE = 0,
  EDITION = 1,
  ADDITION = 2,
}

enum PRESCRIPTION_OBSERVATION_MODE {
  NONE = 0,
  EDITION = 1,
}

enum MEDICAL_REFERRAL_MODE {
  NONE = 0,
  EDITION = 1,
  ADDITION = 2,
}

const getItemsText = (prescriberConfiguration: PrescriberConfiguration) => {
  const items = ['medicamento'];

  if (prescriberConfiguration.HabilitarExame) items.push('exame');
  if (prescriberConfiguration.HabilitarAtestado) items.push('atestado');

  if (items.length === 1) return items[0];

  return items
    .slice(0, -1)
    .join(', ')
    .concat(` ou ${items.slice(-1)}`);
};

function Prescription() {
  const history = useHistory();
  const isMobile = useIsMobile();

  const { setJustifiedInteractions, justifiedInteractions, loadingInteractions } = useInteractionStore();

  const { path } = usePathStore();

  const { setDrawerOpen, setSelectedMenu } = useDrawerStore();
  const {
    setShowByActivePrinciple,
    prescriberConfiguration,
    addMedicineToPrescription,
    prescription,
    removeMedicineFromPrescription,
    updatePrescriptionMedicine,
    addManualMedicineToPrescription,
    updatePrescriptionManualMedicine,
    removePrescriptionManualMedicine,
    removeMedicalReportFromPrescription,
    updatePrescriptionMedicalReport,
    addMedicalReportToPrescription,
    addMedicalReferralToPrescription,
    updateMedicalReferral,
    removeMedicalReferralFromPrescription,
    addExamToPrescription,
    removeExamFromPrescription,
    updatePrescriptionExam,
    removeMedicalCertificateFromPrescription,
    addMedicalCertificateToPrescription,
    updatePrescriptionMedicalCertificate,
    onUpdatePrescriptionObservation,
    cancelOrContinueLater,
    prescriberConfigurations,
  } = usePrescriptionStore();
  const { generalExamIcd, generalExamObservation, setDefaultExamIndex, setExamIcdHasError, setExamObservationError } =
    useExamStore();

  const prescriber = usePrescriptionStore((store) => store.prescriber());
  const benefit = usePrescriptionStore((store) => store.benefit());
  const prescriptionItemsAmount = usePrescriptionStore((store) => store.prescriptionItemsAmount());
  const partnerJustifyInteraction = usePrescriptionStore((store) => store.partnerJustifyInteraction());
  const enableTussCodeOnInterface = usePrescriptionStore((store) => store.enableTussCodeOnInterface());
  const prescriptionObservation = usePrescriptionStore((store) => store.prescriptionObservation());
  const showByActivePrinciple = usePrescriptionStore((store) => store.showByActivePrinciple());

  const { searchType, setSearchType: changeSearchBarType } = useSearchTypeStore();

  const { setInteractionId, setMedicineId, medicineDrugInteractions } = useInteractionStore();

  const { askForConfirmation, openBaseDialog } = useContext(FeedbackContext);

  const { setSmrForm, smrForm, resetSteps } = useSmrStore();
  const { setApacBpaForms, resetApacBpaForm } = useApacBpaStore();

  const sideBarWidth = useMyCentralStore((state) => state.sideBarWidth);

  const [editionMedicine, setEditionMedicine] = useState<{ medicine: Medicine; index: number } | null>(null);
  const [addingMedicicine, setAddMedicine] = useState<{
    medicine: Medicine;
    searchMedicineId?: number;
    index: number;
    isFromConcomitanceSidebar?: boolean;
  } | null>(null);
  const [editionManualMedicine, setEditionManualMedicine] = useState<{ medicine: ManualMedicine; index: null } | null>(
    null
  );
  const [addingManualMedicicine, setAddManualMedicine] = useState<{ medicine: ManualMedicine; index: null } | null>(
    null
  );

  const [prescriptionObservationMode, setPrescriptionObservationMode] = useState<PRESCRIPTION_OBSERVATION_MODE>(
    PRESCRIPTION_OBSERVATION_MODE.NONE
  );

  const [medicalReferralMode, setMedicalReferralMode] = useState<MEDICAL_REFERRAL_MODE>(MEDICAL_REFERRAL_MODE.NONE);
  const [editingMedicalReferral, setEditingMedicalReferral] = useState<MedicalReferral | null>(null);

  const [medicalCertificateMode, setMedicalCertificateMode] = useState<MEDICAL_CERTIFICATE_MODE>(
    MEDICAL_CERTIFICATE_MODE.NONE
  );
  const [editingMedicalCertificate, setEditingMedicalCertificate] = useState<MedicalCertificate | null>(null);

  const [medicalReportMode, setMedicalReportMode] = useState<MEDICAL_REPORT_MODE>(MEDICAL_REPORT_MODE.NONE);

  const [editingMedicalReport, setEditingMedicalReport] = useState<MedicalReport | null>(null);

  const [documentsLoading, setDocumentsLoading] = useState(false);
  const [showIncompleteModal, setShowIncompleteModal] = useState(false);
  const [, setUserTryingToContinue] = useState(false);
  const [showPspModal, setShowPspModal] = useState(false);
  const [showPspSidebar, setShowPspSidebar] = useState(false);
  const [pspSidebarInitialStep, setPspSidebarInitialStep] = useState<
    'consent' | 'review' | 'success' | 'error' | 'alreadyRegistered' | undefined
  >(undefined);
  const [pendingContinue, setPendingContinue] = useState(false);
  const [pendingPspMedicineData, setPendingPspMedicineData] = useState<{
    medicineData: any;
    searchMedicineId?: number;
    eans?: string[];
    programData?: PspProgramData;
  } | null>(null);

  const [showPbmModal, setShowPbmModal] = useState(false);
  const [showPbmSidebar, setShowPbmSidebar] = useState(false);
  const [pbmSidebarInitialStep, setPbmSidebarInitialStep] = useState<
    'consent' | 'review' | 'success' | 'error' | 'alreadyRegistered' | undefined
  >(undefined);
  const [pendingPbmMedicineData, setPendingPbmMedicineData] = useState<{
    medicineData: Medicine;
    eans?: string[];
    programData?: PspProgramData;
  } | null>(null);

  const [showWhatsappPbmModal, setShowWhatsappPbmModal] = useState(false);
  const [showWhatsappPbmSidebar, setShowWhatsappPbmSidebar] = useState(false);
  const [pendingWhatsappPbmMedicine, setPendingWhatsappPbmMedicine] = useState<Medicine | MedicineForEdition | null>(
    null
  );

  const confirmExamEditsRefs = useRef<Record<number, () => Promise<void>>>({});

  const registerExamConfirmCallback = (index: number, cb: () => Promise<void>) => {
    confirmExamEditsRefs.current[index] = cb;
  };
  const [needsLaterality, setNeedsLaterality] = useState(false);
  const [isLateralityFilled, setIsLateralityFilled] = useState(false);

  const searchInputRef = useRef<HTMLInputElement>(null);

  const { allItems } = useContext(PrescriptionContext);

  const [allItemsToSend, setAllItemsToSend] = useState(allItems);
  const { isFormulaFormDialogOpen, setIsFormulaFormDialogOpen } = usePrescriptionStore();
  const [selectedFormula, setSelectedFormula] = useState<Formula | null>(null);

  const focusOnInput = useCallback(() => {
    if (searchInputRef && searchInputRef.current && searchInputRef.current.focus) {
      searchInputRef.current.focus();
    }
  }, [searchInputRef]);

  useEffect(() => {
    if ([SEARCH_TYPE.EXAMS, SEARCH_TYPE.MEDICINE].includes(searchType)) {
      focusOnInput();
    }
  }, [focusOnInput, searchType]);

  const medicines = prescription?.Itens;
  const exams = prescription?.Exames;
  const formulas = prescription?.Formulas;
  const medicalReferrals = prescription?.Encaminhamentos;
  const medicalCertificates = prescription?.Atestados;
  const medicalReports = prescription?.Relatorios;

  const enableAudioPrescription = prescription?.PrescricaoPorAudio?.Habilitado || false;
  const enableAudioPrescriptionOnMobileApp = prescription?.PrescricaoPorAudio?.HabilitadoMobile || false;

  const doctorPermissions = prescription?.Prescritor?.Configuracoes?.permissions;

  const validDoctorPermissions =
    doctorPermissions &&
    Object.values(doctorPermissions).filter(
      (permission) => permission !== undefined && permission !== null && typeof permission === 'boolean'
    );
  const allPermissionsFalse = Object.values(validDoctorPermissions || {}).every((permission) => permission === false);
  const hasCRMorCRO = prescriberConfigurations?.Conselho === 'CRM' || prescriberConfigurations?.Conselho === 'CRO';

  const loadAndReturnSmrForm = useCallback(
    async (prescriptionId: number) => {
      try {
        const form = await PrescriptionService.getSMRForm(prescriptionId);
        setSmrForm(form);
        resetSteps();
        return form;
      } catch (e) {
        logger.error(e);
        return null;
      }
    },
    [setSmrForm, resetSteps]
  );

  const loadApacBpaForm = useCallback(
    async (prescriptionId: number) => {
      try {
        const form = await PrescriptionService.getApacBpaForm(prescriptionId);
        setApacBpaForms(form);
        resetSteps();
        return form;
      } catch (error) {
        logger.error(error);
        return null;
      }
    },
    [resetSteps, setApacBpaForms]
  );

  const validateExamsAndPosologyForContinue = useCallback(async (): Promise<boolean> => {
    if (!prescription) return false;

    const mustSetCid =
      (get(prescriberConfiguration, 'Features.enableMandatoryExamsCid', false) ||
        get(prescriberConfiguration, 'Features.enableSADT', false)) &&
      prescription.Exames.length > 0;
    const mustSetObs =
      (get(prescriberConfiguration, 'Features.enableMandatoryClinicalIndication', false) ||
        get(prescriberConfiguration, 'Features.enableSADT', false)) &&
      prescription.Exames.length > 0;
    const mustSetQuantity =
      get(prescriberConfiguration, 'Features.enableSADT', false) && prescription.Exames.length > 0;

    if (mustSetCid && !generalExamIcd && !allExamsHaveSpecificIcd(prescription.Exames)) {
      setExamIcdHasError(true);
      toast.error('É necessário informar o CID do exame.');
      return false;
    }

    if (mustSetObs && (!generalExamObservation || generalExamObservation.trim() === '')) {
      setExamObservationError(true);
      toast.error('É necessário informar a indicação clínica do exame.');
      return false;
    }

    if (mustSetQuantity && !prescription.Exames.some((exam) => exam.QuantidadeSessoes)) {
      toast.error('É necessário informar a quantidade solicitada do exame.');
      return false;
    }

    if (prescription.Exames.length > 0 && (generalExamObservation || generalExamIcd)) {
      try {
        const payload = {
          Instrucoes: generalExamObservation || '',
          ...(generalExamIcd && {
            CID10Estruturado: {
              Codigo: generalExamIcd.Codigo,
              idCID10: generalExamIcd.idCID10,
              Descricao: generalExamIcd.Descricao,
            },
          }),
        };

        await ExamService.updateGeneralDataExam(prescription.idPrescricao, payload);
      } catch (error) {
        logger.error(error);
        toast.error('Erro ao salvar indicação clínica. Tente novamente.');
        return false;
      }
    }

    setUserTryingToContinue((prev) => !prev);

    await new Promise((resolve) => setTimeout(resolve, 0));

    for (const confirm of Object.values(confirmExamEditsRefs.current)) {
      await confirm();
    }

    if (prescriptionItemsAmount === 0 && !!prescriberConfiguration) {
      const itemsText = getItemsText(prescriberConfiguration);
      toast.error(`Nenhum item encontrado. Insira um ${itemsText} para continuar.`);
      return false;
    }

    const medicinesWithoutPosology = allItems.allMedicines.filter((medicine) => {
      return !medicine.Posologia && !isCorrelatoMedicine(medicine);
    });

    if (medicinesWithoutPosology.length > 0) {
      const medicineNames = medicinesWithoutPosology.map((medicine) => medicine.Label || medicine.Nome).join(', ');

      toast.error(`É necessário preencher a posologia dos seguintes medicamentos antes de continuar: ${medicineNames}`);
      return false;
    }

    return true;
  }, [prescription, prescriberConfiguration, generalExamIcd, generalExamObservation, prescriptionItemsAmount, allItems.allMedicines, setExamIcdHasError, setExamObservationError]);

  const finishPrescriptionFlow = useCallback(async () => {
    if (!prescription) return;

    if (partnerJustifyInteraction) {
      const interactions = await (
        await PrescriptionService.getInteractionsToJustify(prescription.idPrescricao)
      ).filter((interaction) => interaction.requiredAction !== 'N/A');
      setJustifiedInteractions(interactions);

      const isAllJustified = interactions.every((item) => item.justification);

      if ((!isEmpty(interactions) && !isEqual(interactions, justifiedInteractions)) || !isAllJustified) {
        let shouldContinue = false;

        await openBaseDialog(
          (dialogProps: DialogWrappedComponentProps) => {
            const onClose = () => {
              dialogProps.onClose();
            };

            const onComplete = () => {
              dialogProps.onClose();
              shouldContinue = true;
            };

            return <JustifyInteractionsDialog onComplete={onComplete} interactions={interactions} onClose={onClose} />;
          },
          {
            width: isMobile ? '95vw' : '600px',
            height: '450px',
            backgroundColor: 'transparent',
            onCloseCallback: () => {
              shouldContinue = false;
            },
          }
        );

        if (!shouldContinue) return;
      }
    }

    if (medicalCertificateMode !== MEDICAL_CERTIFICATE_MODE.NONE) {
      if (
        !(await askForConfirmation({
          confirmText: 'Continuar',
          cancelText: 'Cancelar',
          text: 'O Atestado que está em edição será perdido.',
          title: 'Certeza que quer continuar?',
        }))
      ) {
        return;
      }
    }

    if (prescriptionObservationMode !== PRESCRIPTION_OBSERVATION_MODE.NONE) {
      if (
        !(await askForConfirmation({
          confirmText: 'Continuar',
          cancelText: 'Cancelar',
          text: 'A observação que está em edição será perdida',
          title: 'Certeza que quer continuar?',
        }))
      ) {
        return;
      }
    }

    setDocumentsLoading(true);

    if (prescription.Exames?.length > 0) {
      const needToFillLaterality = prescription.Exames.some((exam) => {
        return exam.LadoRequerido && !exam.Lateralidade;
      });

      if (needToFillLaterality && !isLateralityFilled) {
        setDocumentsLoading(false);
        toast.error('Por favor, preencha a lateralidade do exame para continuar');
        setNeedsLaterality(true);
        return;
      }
    }

    const needsToFillEspecificDate = prescription.Exames.some(
      (exam) => exam.QuandoRealizar === EXAM_PERIOD_OPTIONS[3] && !exam.DataEspecifica
    );

    const wrongEspecificDate = prescription.Exames.some(
      (exam) => exam.DataEspecifica && DateFromJSDate(exam.DataEspecifica) < DateFromJSDate(new Date())
    );

    if (wrongEspecificDate) {
      setDocumentsLoading(false);
      toast.error('A data específica do exame não pode ser anterior a hoje');
      return;
    }

    if (needsToFillEspecificDate) {
      setDocumentsLoading(false);
      toast.error('Por favor, preencha a data específica do exame para continuar');
      return;
    }

    const hasLme = prescription.Itens?.some((medicine) => medicine.medicamentoLme);

    if (hasLme) {
      const smrForm =
        prescription && prescription.idPrescricao ? await loadAndReturnSmrForm(prescription.idPrescricao) : null;

      if (smrForm !== null && smrForm.prescricaoCidLme && smrForm.prescricaoCidLme.length > 0) {
        history.push(`${path}/lme`);
      }
      setDocumentsLoading(false);
      return;
    }

    const hasApacOrBpa = prescription.Exames.some(
      (exame) => exame?.tipoExameFormulario && exame?.tipoExameFormulario?.length > 0
    );
    if (hasApacOrBpa) {
      const apacBpaForm = await loadApacBpaForm(prescription.idPrescricao);

      if (apacBpaForm !== null) {
        history.push(`${path}/apac-bpa`);
        return;
      }
      setDocumentsLoading(false);
    }

    if (!hasLme && smrForm) {
      setSmrForm(null);
      resetSteps();
    }
    Tracker.trackEvent({
      event: 'ContinueToFinish',
    });
    history.push(`${path}/emission`);
  }, [prescription, partnerJustifyInteraction, medicalCertificateMode, prescriptionObservationMode, smrForm, history, path, setJustifiedInteractions, justifiedInteractions, openBaseDialog, isMobile, askForConfirmation, isLateralityFilled, loadAndReturnSmrForm, loadApacBpaForm, setSmrForm, resetSteps]);

  const onContinueFooter = useCallback(async () => {
    if (!prescription) return;

    const validated = await validateExamsAndPosologyForContinue();
    if (!validated) return;

    const enablePspBenefit = get(prescriberConfiguration, 'Features.enablePspBenefit', false);

    if (enablePspBenefit && prescription.Itens && prescription.Itens.length > 0) {
      const medicinesWithPspNotInitiated = prescription.Itens.filter((medicine) => {
        const hasPspBenefit =
          medicine.Beneficios?.some((b) => b.Tipo === BENEFIT_TYPE.INDUSTRIA && b.Descricao?.startsWith?.('PSP')) ||
          false;
        const notInitiated = medicine.pspInitiated === undefined || medicine.pspInitiated === false;
        return hasPspBenefit && notInitiated;
      });

      if (medicinesWithPspNotInitiated.length > 0) {
        const medicinesWithEans = medicinesWithPspNotInitiated.filter(
          (medicine) => medicine?.ListaEANS && Array.isArray(medicine.ListaEANS) && medicine.ListaEANS.length > 0
        );

        const lookupResults = await Promise.all(
          medicinesWithEans.map(async (medicine) => {
            if (!prescription?.Paciente?.Documento) {
              return { medicine, programData: null, error: null };
            }

            try {
              if (medicine.pspProgramData) {
                return { medicine, programData: medicine.pspProgramData, error: null };
              }

              const programData = await MedicineService.pspCpfLookup({
                document: prescription.Paciente.Documento,
                eans: medicine.ListaEANS,
                prescriptionId: prescription.idPrescricao,
                patientId: prescription.Paciente.idPaciente,
                doctorId: prescriber?.idPrescritor ?? 0,
              });

              if (!programData?.program) {
                setPspSidebarInitialStep('error');
                setPendingPspMedicineData({
                  medicineData: medicine,
                  eans: medicine.EANs || medicine.ListaEANS || [],
                  programData: undefined,
                });
                setShowPspSidebar(true);
                return { medicine, programData: null, error: 'program_not_found' };
              }

              return { medicine, programData, error: null };
            } catch (error) {
              logger.error('[PSP - CPF Lookup] Erro ao buscar dados do programa PSP:', error);
              setPspSidebarInitialStep('error');
              setPendingPspMedicineData({
                medicineData: medicine,
                eans: medicine.EANs || medicine.ListaEANS || [],
                programData: undefined,
              });
              setShowPspSidebar(true);
              return { medicine, programData: null, error };
            }
          })
        );

        for (const { medicine, programData, error } of lookupResults) {
          try {
            await TrackerEventService.track(prescription.idPrescricao, 'backoffice_campaign_psp_cta_continuar_click', {
              flow: 'assinar prescrição',
              idPrescricao: prescription.idPrescricao,
              codigoValidacao: prescription.CodigoValidacao,
              idParceiro: prescriberConfiguration?.idParceiro,
              idMedico: prescriber?.idPrescritor,
              idPaciente: prescription.Paciente?.idPaciente,
              idMedicamento: medicine.IdMedicamento,
              listaEans: medicine.EANs ?? medicine.ListaEANS ?? [],
              nomeDoPrograma: getPspBenefit(medicine.Beneficios)?.Nome,
              ambiente: process.env.REACT_APP_ENVIRONMENT || 'staging',
              isMobile: isMobile,
              statusCadastro: programData?.registered ? 'cadastrado' : 'nao_cadastrado',
            });
          } catch (error) {
            logger.error('Erro ao fazer tracking do evento PSP:', error);
          }

          if (error) {
            const updatedMedicine = { ...medicine, pspInitiated: true };
            await updatePrescriptionMedicine(updatedMedicine);
            continue;
          }

          if (!programData?.program || programData?.registered) {
            const updatedMedicine = { ...medicine, pspInitiated: true };
            await updatePrescriptionMedicine(updatedMedicine);
            continue;
          }

          setPendingPspMedicineData({
            medicineData: medicine,
            eans: medicine.EANs || medicine.ListaEANS || [],
            programData: programData || undefined,
          });

          setShowPspModal(true);
          setPendingContinue(true);
          return;
        }
      }
    }

    const enablePbmBenefit = get(prescriberConfiguration, 'Features.enablePbmBenefit', false);
    if (shouldShowPbmRescue(enablePbmBenefit, allItems.allMedicines as Medicine[])) {
      const medicinesWithPbmNotConsented = (allItems.allMedicines as Medicine[]).filter(
        (medicine) =>
          !!getPbmBenefit(medicine.Beneficios) &&
          medicine.pbmConsentGiven !== true &&
          medicine.pbmRescueDismissed !== true
      );

      const medicinesWithEans = medicinesWithPbmNotConsented.filter(
        (medicine) => medicine?.ListaEANS && Array.isArray(medicine.ListaEANS) && medicine.ListaEANS.length > 0
      );

      const lookupResults = await Promise.all(
        medicinesWithEans.map(async (medicine) => {
          if (!prescription?.Paciente?.Documento) {
            return { medicine, programData: null, error: null };
          }

          try {
            if (medicine.pbmProgramData) {
              return { medicine, programData: medicine.pbmProgramData, error: null };
            }

            const programData = await PbmService.pbmCpfLookup({
              document: prescription.Paciente.Documento,
              eans: medicine.ListaEANS,
              prescriptionId: prescription.idPrescricao,
              patientId: prescription.Paciente.idPaciente,
              doctorId: prescriber?.idPrescritor ?? 0,
            });

            if (!programData?.program) {
              return { medicine, programData: null, error: 'program_not_found' };
            }

            return { medicine, programData, error: null };
          } catch (error) {
            // Na emissão, se o lookup falhar, dispensamos o resgate e seguimos: a receita
            // pode ser emitida normalmente. O tratamento acontece no loop abaixo.
            logger.error('[PBM - CPF Lookup] Erro ao buscar dados do programa PBM:', error);
            return { medicine, programData: null, error };
          }
        })
      );

      for (const { medicine, programData, error } of lookupResults) {
        try {
          await TrackerEventService.track(prescription.idPrescricao, 'backoffice_campaign_pbm_cta_continue_click', {
            flow: 'assinar prescrição',
            idPrescricao: prescription.idPrescricao,
            codigoValidacao: prescription.CodigoValidacao,
            idParceiro: prescriberConfiguration?.idParceiro,
            idMedico: prescriber?.idPrescritor,
            idPaciente: prescription.Paciente?.idPaciente,
            idMedicamento: medicine.IdMedicamento,
            listaEans: medicine.EANs ?? medicine.ListaEANS ?? [],
            nomeDoPrograma: getPbmBenefit(medicine.Beneficios)?.Nome,
            isMobile: isMobile,
            tipoDePrograma: 'PBM',
            statusCadastro: programData?.registered ? 'cadastrado' : 'nao_cadastrado',
          });
        } catch (error) {
          logger.error('Erro ao fazer tracking do evento PBM:', error);
        }

        if (error) {
          usePrescriptionStore.getState().setMedicinePbmRescueDismissed(medicine.IdMedicamento, true);
          await updatePrescriptionMedicine({ ...medicine, pbmRescueDismissed: true });
          continue;
        }

        if (!programData?.program || programData?.registered) {
          usePrescriptionStore.getState().setMedicinePbmRescueDismissed(medicine.IdMedicamento, true);
          await updatePrescriptionMedicine({ ...medicine, pbmRescueDismissed: true });
          continue;
        }

        setPendingPbmMedicineData({
          medicineData: medicine,
          eans: medicine.EANs || medicine.ListaEANS || [],
          programData: programData || undefined,
        });

        setShowPbmModal(true);
        setPendingContinue(true);
        return;
      }
    }

    const enableWhatsappPbmBenefit = get(prescriberConfiguration, 'Features.enableWhatsappPbmBenefit', false);
    if (shouldShowWhatsappPbmRescue(enableWhatsappPbmBenefit, allItems.allMedicines as Medicine[])) {
      const medicineToRescue = (allItems.allMedicines as Medicine[]).find(
        (medicine) => !!getWhatsappPbmBenefit(medicine.Beneficios) && medicine.pbmRescueDismissed !== true
      );

      if (medicineToRescue) {
        try {
          await TrackerEventService.track(prescription.idPrescricao, 'backoffice_campaign_pbm_cta_continue_click', {
            flow: 'assinar prescrição',
            idPrescricao: prescription.idPrescricao,
            codigoValidacao: prescription.CodigoValidacao,
            idParceiro: prescriberConfiguration?.idParceiro,
            idMedico: prescriber?.idPrescritor,
            idPaciente: prescription.Paciente?.idPaciente,
            idMedicamento: medicineToRescue.IdMedicamento,
            listaEans: medicineToRescue.EANs ?? medicineToRescue.ListaEANS ?? [],
            nomeDoPrograma: getWhatsappPbmBenefit(medicineToRescue.Beneficios)?.Nome,
            isMobile: isMobile,
            tipoDePrograma: WHATSAPP_PBM_PROGRAM_TYPE,
          });
        } catch (error) {
          logger.error('Erro ao fazer tracking do evento WhatsApp PBM:', error);
        }

        setPendingWhatsappPbmMedicine(medicineToRescue);
        setShowWhatsappPbmModal(true);
        setPendingContinue(true);
        return;
      }
    }

    await finishPrescriptionFlow();
  }, [
    prescription,
    prescriberConfiguration,
    validateExamsAndPosologyForContinue,
    finishPrescriptionFlow,
    isMobile,
    updatePrescriptionMedicine,
    prescriber?.idPrescritor,
    allItems.allMedicines,
  ]);

  const handlePspModalConfirm = async () => {
    setShowPspModal(false);
    if (pendingPspMedicineData?.medicineData) {
      const medicine = prescription?.Itens?.find(
        (m) =>
          m.idPrescricaoMedicamento === pendingPspMedicineData.medicineData.idPrescricaoMedicamento ||
          m.IdMedicamento === pendingPspMedicineData.medicineData.IdMedicamento
      );

      if (medicine) {
        const updatedMedicine = { ...medicine, pspInitiated: true };
        await updatePrescriptionMedicine(updatedMedicine);
      }
    }
    setShowPspSidebar(true);
  };

  const handlePspModalCancel = async () => {
    setShowPspModal(false);
    setPendingContinue(false);
    if (pendingContinue) {
      await finishPrescriptionFlow();
      setPendingContinue(false);
    }
  };

  const handleOpenPspSidebar = useCallback(
    async (medicineData: MedicineForEdition, pspLookupResult?: PspProgramData) => {
      if (!medicineData.EANs || !Array.isArray(medicineData.EANs) || medicineData.EANs.length === 0) {
        return;
      }

      let programData = pspLookupResult ?? null;
      if (prescription?.Paciente?.Documento && !programData) {
        try {
          // se por algum motivo não foi passado o programData, busca na api
          programData = await MedicineService.pspCpfLookup({
            document: prescription.Paciente.Documento,
            eans: medicineData.EANs,
            prescriptionId: prescription.idPrescricao,
            patientId: prescription.Paciente.idPaciente,
            doctorId: prescriber?.idPrescritor ?? 0,
          });
        } catch (error) {
          setPspSidebarInitialStep('error');
          logger.error('[PSP - CPF Lookup] Erro ao buscar dados do programa PSP:', error);
        }
      }

      if (!programData?.program) {
        setPspSidebarInitialStep('error');
        return;
      }

      if (programData && medicineData.IdMedicamento != null) {
        usePrescriptionStore.getState().setMedicinePspProgramData(medicineData.IdMedicamento, programData);
      }

      setPendingPspMedicineData({
        medicineData,
        searchMedicineId: addingMedicicine?.searchMedicineId,
        eans: medicineData.EANs || medicineData.EANs || [],
        programData: programData || undefined,
      });

      const medicine = prescription?.Itens?.find(
        (m) =>
          m.idPrescricaoMedicamento === medicineData.idPrescricaoMedicamento ||
          m.IdMedicamento === medicineData.IdMedicamento
      );

      if (medicine) {
        const updatedMedicine = { ...medicine, pspInitiated: true };
        await updatePrescriptionMedicine(updatedMedicine);
      }

      setShowPspSidebar(true);
    },
    [
      addingMedicicine?.searchMedicineId,
      prescription?.Paciente?.Documento,
      prescription?.Itens,
      updatePrescriptionMedicine,
      prescription?.idPrescricao,
      prescriber?.idPrescritor,
      prescription?.Paciente?.idPaciente,
    ]
  );

  const handlePspSidebarConfirm = async (
    patientConsent: boolean,
    professionalConsent: boolean,
    email?: string,
    phone?: string
  ) => {
    if (pendingPspMedicineData?.medicineData) {
      const medicine = prescription?.Itens?.find(
        (m) =>
          m.idPrescricaoMedicamento === pendingPspMedicineData.medicineData.idPrescricaoMedicamento ||
          m.IdMedicamento === pendingPspMedicineData.medicineData.IdMedicamento
      );

      if (medicine && medicine.pspInitiated !== true) {
        const updatedMedicine = { ...medicine, pspInitiated: true };
        await updatePrescriptionMedicine(updatedMedicine);
      }
    }

    setPendingContinue(false);
    if (pendingContinue) {
      await finishPrescriptionFlow();
      setPendingContinue(false);
    }
  };

  const handlePspSidebarCancel = async () => {
    setPendingPspMedicineData(null);
    setPspSidebarInitialStep(undefined);
    setShowPspSidebar(false);
    setPendingContinue(false);
    if (pendingContinue) {
      await finishPrescriptionFlow();
      setPendingContinue(false);
    }
  };

  const handleOpenPbmSidebar = useCallback(
    async (medicineData: Medicine | MedicineForEdition, pbmLookupResult?: PspProgramData) => {
      if (!medicineData.EANs || !Array.isArray(medicineData.EANs) || medicineData.EANs.length === 0) {
        return;
      }

      // Reseta o passo para não vazar um estado de erro de uma abertura anterior.
      setPbmSidebarInitialStep(undefined);

      const openInErrorState = () => {
        setPbmSidebarInitialStep('error');
        setPendingPbmMedicineData({
          medicineData: medicineData as Medicine,
          eans: medicineData.EANs || [],
          programData: undefined,
        });
        setShowPbmSidebar(true);
      };

      let programData = pbmLookupResult ?? null;
      if (prescription?.Paciente?.Documento && !programData) {
        try {
          // se por algum motivo não foi passado o programData, busca na api
          programData = await PbmService.pbmCpfLookup({
            document: prescription.Paciente.Documento,
            eans: medicineData.EANs,
            prescriptionId: prescription.idPrescricao,
            patientId: prescription.Paciente.idPaciente,
            doctorId: prescriber?.idPrescritor ?? 0,
          });
        } catch (error) {
          logger.error('[PBM - CPF Lookup] Erro ao buscar dados do programa PBM:', error);
          openInErrorState();
          return;
        }
      }

      if (!programData?.program) {
        openInErrorState();
        return;
      }

      if (medicineData.IdMedicamento != null) {
        usePrescriptionStore.getState().setMedicinePbmProgramData(medicineData.IdMedicamento, programData);
      }

      setPendingPbmMedicineData({
        medicineData: medicineData as Medicine,
        eans: medicineData.EANs || [],
        programData,
      });

      setShowPbmSidebar(true);
    },
    [
      prescription?.Paciente?.Documento,
      prescription?.idPrescricao,
      prescription?.Paciente?.idPaciente,
      prescriber?.idPrescritor,
    ]
  );

  const handlePbmModalConfirm = () => {
    setShowPbmModal(false);
    // Abertura legítima: garante que o sidebar não comece em um estado de erro residual.
    setPbmSidebarInitialStep(undefined);
    setShowPbmSidebar(true);
  };

  const handlePbmModalCancel = async () => {
    setShowPbmModal(false);

    if (pendingPbmMedicineData?.medicineData?.IdMedicamento != null) {
      const medicineId = pendingPbmMedicineData.medicineData.IdMedicamento;
      usePrescriptionStore.getState().setMedicinePbmRescueDismissed(medicineId, true);

      const store = usePrescriptionStore.getState();
      const medicineInPrescription = store.prescription?.Itens?.find(
        (m) =>
          m.IdMedicamento === medicineId ||
          (pendingPbmMedicineData.medicineData.idPrescricaoMedicamento != null &&
            m.idPrescricaoMedicamento === pendingPbmMedicineData.medicineData.idPrescricaoMedicamento)
      );

      if (medicineInPrescription) {
        await updatePrescriptionMedicine({ ...medicineInPrescription, pbmRescueDismissed: true });
      }
    }

    setPendingContinue(false);
    if (pendingContinue) {
      await finishPrescriptionFlow();
      setPendingContinue(false);
    }
  };

  const handlePbmSidebarConfirm = async (
    _patientConsent: boolean,
    _professionalConsent: boolean,
    _email?: string,
    _phone?: string
  ) => {
    setPendingContinue(false);
    if (pendingContinue) {
      await finishPrescriptionFlow();
      setPendingContinue(false);
    }
  };

  // Apenas limpa o estado do PBM. Usado no onCancel, que o PbmSidebar dispara no mesmo tick
  // que o onClose (handleCancelConfirm) — manter o finishPrescriptionFlow só no onClose evita
  // executá-lo em dobro.
  const handlePbmSidebarCleanup = () => {
    setPendingPbmMedicineData(null);
    setPbmSidebarInitialStep(undefined);
    setShowPbmSidebar(false);
  };

  const handlePbmSidebarCancel = async () => {
    handlePbmSidebarCleanup();
    setPendingContinue(false);
    if (pendingContinue) {
      await finishPrescriptionFlow();
      setPendingContinue(false);
    }
  };

  const handleOpenWhatsappPbmSidebar = useCallback((medicineData: Medicine | MedicineForEdition) => {
    setPendingWhatsappPbmMedicine(medicineData);
    setShowWhatsappPbmSidebar(true);
  }, []);

  const handleWhatsappPbmModalConfirm = useCallback(() => {
    setShowWhatsappPbmModal(false);
    setShowWhatsappPbmSidebar(true);
  }, []);

  const continuePendingEmission = useCallback(async () => {
    setPendingContinue(false);
    if (pendingContinue) {
      await finishPrescriptionFlow();
    }
  }, [pendingContinue, finishPrescriptionFlow]);

  const handleWhatsappPbmModalCancel = useCallback(async () => {
    setShowWhatsappPbmModal(false);

    // O consentimento é por prescrição: recusar o resgate dispensa todos os itens elegíveis,
    // para o modal não reaparecer para o próximo item na mesma emissão.
    const store = usePrescriptionStore.getState();
    (store.prescription?.Itens ?? [])
      .filter((medicine) => !!getWhatsappPbmBenefit(medicine.Beneficios))
      .forEach((medicine) => store.setMedicinePbmRescueDismissed(medicine.IdMedicamento, true));

    setPendingWhatsappPbmMedicine(null);
    await continuePendingEmission();
  }, [continuePendingEmission]);

  const handleWhatsappPbmSidebarClose = useCallback(async () => {
    setShowWhatsappPbmSidebar(false);
    setPendingWhatsappPbmMedicine(null);
    await continuePendingEmission();
  }, [continuePendingEmission]);

  const isOnMobileApp = isMobileApp();
  const enableAudioPrescriptionAndroidApp = get(
    prescriberConfiguration,
    'Features.enableAudioPrescriptionAndroidApp',
    false
  );

  const incommingStructuredCid = useMemo(() => {
    const cid =
      prescription?.CIDS10Estruturados && prescription?.CIDS10Estruturados.length > 0
        ? prescription.CIDS10Estruturados[0]
        : null;
    return cid !== null
      ? {
        id: cid.idCID10,
        subcategory: cid.Codigo,
        description: cid.Descricao,
      }
      : null;
  }, [prescription?.CIDS10Estruturados]);

  const hideAudioMobileInAndroidApp = isAndroid() && isMobileApp() && !enableAudioPrescriptionAndroidApp;
  const hideAudioMobileInMobileApp =
    (isOnMobileApp && !enableAudioPrescriptionOnMobileApp) || hideAudioMobileInAndroidApp;

  const shouldShowAudioModal = enableAudioPrescription && !hideAudioMobileInMobileApp;
  const showCanceledPrescriptionDialog = useCallback(async () => {
    const isRunningOnIframe = isRunningInIframe();

    return openBaseDialog(
      () => <CanceledPrescription onConfirm={sendCancelPostMessage} showConfirmButton={isRunningOnIframe} />,
      {
        width: appViewportHeight(0.95),
        height: appViewportHeight(0.95),
        persist: true,
        backgroundColor: 'transparent',
      }
    );
  }, [openBaseDialog]);

  const onCancelFooter = useCallback(async () => {
    Tracker.trackEvent({
      event: 'Prescription Trash Icon Clicked',
    });
    if (!prescription) return;

    const hasMedicines = prescription.Itens && prescription.Itens.length > 0;
    const hasExams = prescription.Exames && prescription.Exames.length > 0;
    const hasMedicalCertificates = prescription.Atestados && prescription.Atestados.length > 0;
    const hasMedicalReferrals = prescription.Encaminhamentos && prescription.Encaminhamentos.length > 0;
    const hasMedicalReports = prescription.Relatorios && prescription.Relatorios.length > 0;

    const isPrescriptionEmpty =
      !hasMedicines && !hasExams && !hasMedicalCertificates && !hasMedicalReferrals && !hasMedicalReports;

    if (isPrescriptionEmpty) {
      cancelOrContinueLater(false);
      return;
    }

    const isCancelDisabled = get(prescriberConfiguration, 'Features.disableFinishLater', false);
    const cancelText = isCancelDisabled ? 'Não, voltar à receita' : 'Terminar depois';

    return openBaseDialog(
      (dialogProps: DialogWrappedComponentProps) => (
        <CancelPrescription
          onClose={dialogProps.onClose}
          onConfirm={(reason) => {
            cancelOrContinueLater(true, reason);
            dialogProps.onClose();
            showCanceledPrescriptionDialog();
          }}
          onCancelCallback={() => {
            cancelOrContinueLater(false);
            dialogProps.onClose();
          }}
          cancelText={cancelText}
          confirmText={'Sim, cancelar'}
          disableCancel={isCancelDisabled}
        />
      ),
      {
        width: isMobile ? '95vw' : '400px',
        height: '138px',
        persist: false,
      }
    );
  }, [
    prescription,
    isMobile,
    openBaseDialog,
    cancelOrContinueLater,
    showCanceledPrescriptionDialog,
    prescriberConfiguration,
  ]);

  const cleanEditionAndAdition = useCallback(() => {
    setEditionMedicine(null);
    setAddMedicine(null);

    setAddManualMedicine(null);
    setEditionManualMedicine(null);

    setEditingMedicalReferral(null);
    setEditingMedicalCertificate(null);

    setMedicalCertificateMode(MEDICAL_CERTIFICATE_MODE.NONE);
    setPrescriptionObservationMode(PRESCRIPTION_OBSERVATION_MODE.NONE);
    setMedicalReferralMode(MEDICAL_REFERRAL_MODE.NONE);
    setMedicalReportMode(MEDICAL_REPORT_MODE.NONE);
    setEditingMedicalReport(null);
  }, []);

  const resetSearchBar = useCallback(() => {
    changeSearchBarType(SEARCH_TYPE.MEDICINE);
  }, [changeSearchBarType]);

  const editMedicineRequestIdRef = useRef(0);

  const resolveGenericLabFields = useCallback(
    async (medicine: Medicine | any, medicineId: number, mode: 'adition' | 'edition') => {
      const enableGenericLabSelection = get(prescriberConfiguration, 'Features.enableGenericLabSelection', true);
      if (!enableGenericLabSelection || medicine.TipoMedicamento !== 'GENERICO' || !prescription) return {};

      const { laboratorios, idLaboratorioPatrocinado } = await resolveGenericLabInfo({
        medicineId,
        prescriptionId: prescription.idPrescricao,
        doctorId: prescriber?.idPrescritor ?? 0,
        partnerId: prescriberConfiguration?.idParceiro,
      });

      if (!laboratorios.length) return {};

      const idLaboratorioPreferido =
        mode === 'edition' ? medicine.LaboratorioPreferencia ?? null : idLaboratorioPatrocinado;
      const idLaboratorioSelecionado = laboratorios.some((lab) => lab.id === idLaboratorioPreferido)
        ? idLaboratorioPreferido
        : null;

      return {
        LaboratoriosDisponiveis: laboratorios,
        idLaboratorioSelecionado,
        idLaboratorioPatrocinado,
      };
    },
    [prescription, prescriber, prescriberConfiguration]
  );

  const resolveSelectedLaboratory = useCallback((medicine: Medicine | any) => {
    const selectedLab = medicine.LaboratoriosDisponiveis?.find(
      (lab: { id: number; nome: string }) => lab.id === medicine.idLaboratorioSelecionado
    );

    return {
      laboratory: selectedLab?.nome ?? 'sem preferência',
      isHighlighted: !!selectedLab && selectedLab.id === medicine.idLaboratorioPatrocinado,
    };
  }, []);

  const trackGenericLabEvent = useCallback(
    (eventName: string, flow: string, medicine: Medicine | any) => {
      trackGenericLabEventHelper(eventName, flow, medicine, {
        prescription,
        prescriber,
        prescriberConfiguration,
        isMobile: Boolean(isMobile),
      });
    },
    [prescription, prescriber, prescriberConfiguration, isMobile]
  );

  const persistSelectedLaboratory = useCallback(
    (medicine: Medicine | any) => {
      if (!prescription || !medicine.LaboratoriosDisponiveis?.length || medicine.IdMedicamento == null) return;

      saveSelectedLaboratory(prescription.idPrescricao, medicine.IdMedicamento, resolveSelectedLaboratory(medicine));
    },
    [prescription, resolveSelectedLaboratory]
  );

  const onSelectMedicine = async (medicine: Medicine | any, searchMedicineId?: number) => {
    const medicineId = medicine.IdMedicamento || medicine.medicineId;
    if (!prescription) return;

    const dosage = await MedicineService.fetchDosage(prescription.idPrescricao, searchMedicineId || 0, medicineId);

    const genericLabFields = await resolveGenericLabFields(medicine, medicineId, 'adition');

    trackGenericLabEvent('backoffice_campaign_solution_generic_view', 'adicionar posologia', {
      ...medicine,
      ...genericLabFields,
    });

    setAddMedicine({
      medicine: {
        ...medicine,
        Posologia: dosage,
        Ordem: allItems.nextOrder,
        ...genericLabFields,
      },
      index: (medicines || []).length + 1,
      searchMedicineId,
      isFromConcomitanceSidebar: false,
    });
  };

  const onSelectFormula = async (formula: Formula | null) => {
    if (!prescription) return;

    if (formula) {
      amplitudeModule.compoundedMedicationClicked({
        validation_code: prescription.CodigoValidacao ?? '',
        compounded_medication_id: formula.id ?? 0,
        partner_id: prescriberConfiguration?.idParceiro ?? 0,
        partner_name: prescriberConfiguration?.Nome ?? '',
        establishment_name: prescription?.Estabelecimento?.Nome ?? '',
      });
    }

    setSelectedFormula(formula);
    setIsFormulaFormDialogOpen(true);
  };

  const onIncludeConcomitanceMedicine = async (medicine: Medicine | any, searchMedicineId?: number) => {
    const medicineId = medicine.IdMedicamento || medicine.medicineId;

    if (!prescription?.idPrescricao || medicineId == null) return;

    try {
      const dosage = await MedicineService.fetchDosage(prescription.idPrescricao, searchMedicineId ?? 0, medicineId);

      if (searchMedicineId != null) {
        const originMedicine = prescription.Itens?.find((m) => m.IdMedicamento === searchMedicineId);
        saveConcomitanceOrigin(prescription.idPrescricao, medicineId, {
          hasConcomitance: true,
          originId: searchMedicineId,
          originName: originMedicine?.Nome ?? '',
        });
      }

      const genericLabFields = await resolveGenericLabFields(medicine, medicineId, 'adition');

      trackGenericLabEvent('backoffice_campaign_solution_generic_view', 'adicionar posologia', {
        ...medicine,
        ...genericLabFields,
      });

      setAddMedicine({
        medicine: {
          ...medicine,
          Posologia: dosage,
          Ordem: allItems.nextOrder,
          ...genericLabFields,
        },
        index: (medicines || []).length + 1,
        searchMedicineId,
        isFromConcomitanceSidebar: true,
      });
    } catch (error) {
      logger.error('Erro ao buscar posologia da concomitância de medicamento', error);
    }
  };

  const confirmAddMedicine = async (medicine: Medicine) => {
    try {
      if (!prescription) return;

      await addMedicineToPrescription({
        medicine: {
          ...medicine,
          DataAdicao: DateFromJSDate(new Date()),
        },
        searchMedicineId: addingMedicicine?.searchMedicineId || null,
      });

      persistSelectedLaboratory(medicine);

      if (medicine.IdMedicamento != null) {
        saveStructuredPosology(
          prescription.idPrescricao,
          medicine.IdMedicamento,
          medicine.isStructuredPosology ?? !!medicine.PosologiaEstruturada
        );
      }

      if (medicine.isPremiumPlacement) {
        await TrackerEventService.track(prescription.idPrescricao, 'CVR_item_patrocinado', {
          idPrescricao: prescription.idPrescricao,
          idMedico: prescriber?.idPrescritor,
          idMedicamento: medicine.IdMedicamento,
        });
      }

      trackGenericLabEvent('backoffice_campaign_solution_generic_click', 'inclusão medicamento', medicine);

      setAddMedicine(null);
      focusOnInput();
    } catch (error) { }
  };

  const confirmEditMedicine = async (medicine: Medicine) => {
    try {
      await updatePrescriptionMedicine(medicine);
      persistSelectedLaboratory(medicine);

      if (prescription && medicine.IdMedicamento != null) {
        saveStructuredPosology(
          prescription.idPrescricao,
          medicine.IdMedicamento,
          medicine.isStructuredPosology ?? !!medicine.PosologiaEstruturada
        );
      }

      trackGenericLabEvent('backoffice_campaign_solution_generic_click', 'edição medicamento', medicine);

      setEditionMedicine(null);
      focusOnInput();
    } catch (error) { }
  };

  const cancelAddMedicine = () => {
    setAddMedicine(null);
  };

  const onRemoveMedicine = async (medicine: Medicine) => {
    try {
      amplitudeModule.removePrescriptionItemClicked({
        partner_id: prescriberConfiguration?.idParceiro ?? 0,
        partner_name: prescriberConfiguration?.Nome ?? '',
        establishment_name: prescription?.Estabelecimento?.Nome ?? '',
        medication: [
          {
            id: medicine.IdMedicamento ?? 0,
            name: medicine.Nome ?? medicine.Label ?? '',
            is_sponsored: medicine.isPremiumPlacement ?? false,
            is_voice_prescribed: medicine.AdicionadoPorIA ?? false,
          },
        ],
      });
      await removeMedicineFromPrescription(medicine);
    } catch (error) { }
  };

  const onEditMedicine = useCallback(
    async (props: { medicine: Medicine; index: number }) => {
      import('../../shared/eventTracking').then(({ trackingService }) => {
        trackingService.trackPrescriptionItemEdit(props.medicine);
      });

      amplitudeModule.editPrescriptionItemClicked({
        partner_id: prescriberConfiguration?.idParceiro ?? 0,
        partner_name: prescriberConfiguration?.Nome ?? '',
        establishment_name: prescription?.Estabelecimento?.Nome ?? '',
        medication: [
          {
            id: props.medicine.IdMedicamento ?? 0,
            name: props.medicine.Nome ?? props.medicine.Label ?? '',
            is_sponsored: props.medicine.isPremiumPlacement ?? false,
            is_voice_prescribed: props.medicine.AdicionadoPorIA ?? false,
          },
        ],
      });

      changeSearchBarType(SEARCH_TYPE.MEDICINE);
      cleanEditionAndAdition();

      const requestId = ++editMedicineRequestIdRef.current;
      const genericLabFields = await resolveGenericLabFields(props.medicine, props.medicine.IdMedicamento, 'edition');
      if (requestId !== editMedicineRequestIdRef.current) return;

      trackGenericLabEvent('backoffice_campaign_solution_generic_view', 'editar posologia', {
        ...props.medicine,
        ...genericLabFields,
      });

      setEditionMedicine({
        medicine: { ...props.medicine, ...genericLabFields },
        index: props.index,
      });
    },
    [
      changeSearchBarType,
      cleanEditionAndAdition,
      prescriberConfiguration,
      prescription?.Estabelecimento?.Nome,
      resolveGenericLabFields,
      trackGenericLabEvent,
    ]
  );

  const onCancelEditMedicine = useCallback(() => {
    setEditionMedicine(null);
  }, []);

  const onSelectExam = useCallback(
    async ({ exam, searchExamLogId }: { exam: Exam; searchExamLogId: number | null }) => {
      if (!prescription) return;

      if (searchExamLogId) {
        ExamService.sendClickEvent({
          logId: searchExamLogId,
          examId: exam.idEzOrder,
        }).catch((error) => logger.error(error));
      }

      const payload: Exam = {
        ...exam,
        AplicarAnestesia: false,
        Instrucoes: generalExamObservation || '',
        Lateralidade: null,
        ...(generalExamIcd && {
          CID10Estruturado: [generalExamIcd],
        }),
      };

      await addExamToPrescription({ exam: payload, searchExamLogId: searchExamLogId });
    },
    [addExamToPrescription, generalExamIcd, generalExamObservation, prescription]
  );

  const showSearch = [
    !addingMedicicine,
    !editionMedicine,
    !addingManualMedicicine,
    !editionManualMedicine,
    medicalCertificateMode === MEDICAL_CERTIFICATE_MODE.NONE,
    medicalReferralMode === MEDICAL_REFERRAL_MODE.NONE,
    prescriptionObservationMode === PRESCRIPTION_OBSERVATION_MODE.NONE,
    medicalReportMode === MEDICAL_REPORT_MODE.NONE,
  ].every((v) => !!v);

  const onDeleteExam = async (exam: Exam) => {
    try {
      amplitudeModule.removePrescriptionItemClicked({
        partner_id: prescriberConfiguration?.idParceiro ?? 0,
        partner_name: prescriberConfiguration?.Nome ?? '',
        establishment_name: prescription?.Estabelecimento?.Nome ?? '',
        medical_test: [
          {
            id: exam.CodigoTUSS ?? exam.idEzOrder?.toString() ?? '',
            name: exam.Nome ?? exam.Label ?? '',
            is_voice_prescribed: exam.AdicionadoPorIA ?? false,
          },
        ],
      });
      await removeExamFromPrescription(exam);
      resetApacBpaForm();
    } catch (error) { }
  };

  const onEditExam = useCallback(
    (index: number) => {
      if (prescription?.Exames && prescription.Exames[index]) {
        const exam = prescription.Exames[index];

        import('../../shared/eventTracking').then(({ trackingService }) => {
          trackingService.trackPrescriptionItemEdit(exam);
        });

        amplitudeModule.editPrescriptionItemClicked({
          partner_id: prescriberConfiguration?.idParceiro ?? 0,
          partner_name: prescriberConfiguration?.Nome ?? '',
          establishment_name: prescription?.Estabelecimento?.Nome ?? '',
          medical_test: [
            {
              id: exam.CodigoTUSS ?? exam.idEzOrder?.toString() ?? '',
              name: exam.Nome ?? exam.Label ?? '',
              is_voice_prescribed: exam.AdicionadoPorIA ?? false,
            },
          ],
        });
      }

      changeSearchBarType(SEARCH_TYPE.EXAMS);
      setDefaultExamIndex(index);
    },
    [changeSearchBarType, setDefaultExamIndex, prescription, prescriberConfiguration]
  );

  const onSelectManualMedicine = (manualMedicineName: string) => {
    setAddManualMedicine({
      medicine: {
        Embalagem: null,
        Quantidade: null,
        Posologia: '',
        Label: manualMedicineName,
        Manual: true,
        idMedicamentoManual: null,
        idPrescricaoMedicamentoManual: null,
        Nome: manualMedicineName,
        UsoContinuo: false,
        QuantidadeDispensada: null,
        Dispensado: false,
        DetalhesDispensa: [],
        UsoInterno: false,
        LME: false,
        ProdutoCorrelato: false,
        Ordem: allItems.nextOrder,
        Manipulado: false,
        TipoReceita: PT_PRESCRIPTION_TYPE.SIMPLES,
      },
      index: null,
    });
  };

  const onConfirmAddManualMedicine = async (manualMedicine: ManualMedicine) => {
    try {
      await addManualMedicineToPrescription({
        ...manualMedicine,
        DataAdicao: DateFromJSDate(new Date()),
      });
      setAddManualMedicine(null);
      focusOnInput();
    } catch (error) { }
  };

  const onCancelAddManualMedicine = () => {
    setAddManualMedicine(null);
  };

  const onConfirmEditManualMedicine = async (manualMedicine: ManualMedicine) => {
    try {
      await updatePrescriptionManualMedicine(manualMedicine);
      setEditionManualMedicine(null);
    } catch (error) { }
  };

  const onCancelEditManualMedicine = () => {
    setEditionManualMedicine(null);
  };

  const onEditManualMedicine = (props: { medicine: ManualMedicine; index: null }) => {
    import('../../shared/eventTracking').then(({ trackingService }) => {
      trackingService.trackPrescriptionItemEdit(props.medicine);
    });

    amplitudeModule.editPrescriptionItemClicked({
      partner_id: prescriberConfiguration?.idParceiro ?? 0,
      partner_name: prescriberConfiguration?.Nome ?? '',
      establishment_name: prescription?.Estabelecimento?.Nome ?? '',
      medication: [
        {
          id: props.medicine.idMedicamentoManual ?? 0,
          name: props.medicine.Nome ?? props.medicine.Label ?? '',
          is_sponsored: false,
          is_voice_prescribed: props.medicine.AdicionadoPorIA ?? false,
        },
      ],
    });

    cleanEditionAndAdition();
    setEditionManualMedicine({
      medicine: props.medicine,
      index: props.index,
    });
  };

  const onRemoveManualMedicine = async (manualMedicine: ManualMedicine) => {
    try {
      amplitudeModule.removePrescriptionItemClicked({
        partner_id: prescriberConfiguration?.idParceiro ?? 0,
        partner_name: prescriberConfiguration?.Nome ?? '',
        establishment_name: prescription?.Estabelecimento?.Nome ?? '',
        medication: [
          {
            id: manualMedicine.idMedicamentoManual ?? 0,
            name: manualMedicine.Nome ?? manualMedicine.Label ?? '',
            is_sponsored: false,
            is_voice_prescribed: manualMedicine.AdicionadoPorIA ?? false,
          },
        ],
      });
      await removePrescriptionManualMedicine(manualMedicine);
    } catch (error) { }
  };

  const onClickInteraction = (interactionId: string, medicineId: number) => {
    setSelectedMenu('DRUG_INTERACTIONS');
    setInteractionId(interactionId);
    setMedicineId(medicineId);
    setDrawerOpen(true);

    const allMedicines = prescription?.Itens ?? [];
    const clickedMedicine = allMedicines.find((m) => m.IdMedicamento === medicineId);
    const interactionData = medicineDrugInteractions.find((m) => m.medicineId === medicineId);

    const medications: { id: number; name: string; is_sponsored: boolean }[] = [];

    if (clickedMedicine) {
      medications.push({
        id: clickedMedicine.IdMedicamento ?? 0,
        name: clickedMedicine.Nome ?? clickedMedicine.Label ?? '',
        is_sponsored: clickedMedicine.isPremiumPlacement ?? false,
      });
    }

    if (interactionData) {
      interactionData.drugInteractions.forEach((drug) => {
        drug.medicineDrugInteractions.forEach((interaction) => {
          if (interaction.medicineId !== medicineId) {
            const interactingMedicine = allMedicines.find((m) => m.IdMedicamento === interaction.medicineId);
            medications.push({
              id: interaction.medicineId,
              name: interaction.medicineName ?? '',
              is_sponsored: interactingMedicine?.isPremiumPlacement ?? false,
            });
          }
        });
      });
    }

    amplitudeModule.drugInteractionClicked({
      partner_id: prescriberConfiguration?.idParceiro ?? 0,
      partner_name: prescriberConfiguration?.Nome ?? '',
      establishment_name: prescription?.Estabelecimento?.Nome ?? '',
      medications,
    });
  };

  const onEditMedicalCertificate = useCallback(
    (medicalCertificate: MedicalCertificate) => {
      amplitudeModule.editPrescriptionItemClicked({
        partner_id: prescriberConfiguration?.idParceiro ?? 0,
        partner_name: prescriberConfiguration?.Nome ?? '',
        establishment_name: prescription?.Estabelecimento?.Nome ?? '',
        doctors_note: [
          {
            id: medicalCertificate.idPrescricaoAtestado,
            name: (MEDICAL_CERTIFICATE_TYPES as Record<string, string>)[medicalCertificate.Tipo] ?? '',
          },
        ],
      });

      cleanEditionAndAdition();
      changeSearchBarType(SEARCH_TYPE.MEDICAL_CERTIFICATE);
      setMedicalCertificateMode(MEDICAL_CERTIFICATE_MODE.EDITION);
      setEditingMedicalCertificate(medicalCertificate);
    },
    [changeSearchBarType, cleanEditionAndAdition, prescriberConfiguration, prescription?.Estabelecimento?.Nome]
  );

  const onDeleteMedicalCertificate = useCallback(
    async (medicalCertificateId: number) => {
      try {
        const cert = prescription?.Atestados?.find(
          (a: MedicalCertificate) => a.idPrescricaoAtestado === medicalCertificateId
        );
        amplitudeModule.removePrescriptionItemClicked({
          partner_id: prescriberConfiguration?.idParceiro ?? 0,
          partner_name: prescriberConfiguration?.Nome ?? '',
          establishment_name: prescription?.Estabelecimento?.Nome ?? '',
          doctors_note: [
            {
              id: medicalCertificateId,
              name: cert ? ((MEDICAL_CERTIFICATE_TYPES as Record<string, string>)[cert.Tipo] ?? '') : '',
            },
          ],
        });
        await removeMedicalCertificateFromPrescription(medicalCertificateId);
      } catch (error) { }
    },
    [removeMedicalCertificateFromPrescription, prescription, prescriberConfiguration]
  );

  const onClickToAddMedicalCertificate = useCallback(() => {
    if (!prescription) return;

    cleanEditionAndAdition();
    setMedicalCertificateMode(MEDICAL_CERTIFICATE_MODE.ADDITION);
  }, [cleanEditionAndAdition, prescription]);

  const onCancelEditMedicalCertificate = useCallback(() => {
    resetSearchBar();
    setMedicalCertificateMode(MEDICAL_CERTIFICATE_MODE.NONE);
    setEditingMedicalCertificate(null);
  }, [resetSearchBar]);

  const onConfirmAddMedicalCertificate = useCallback(
    async (medicalCertificate: MedicalCertificateDTO) => {
      try {
        resetSearchBar();
        await addMedicalCertificateToPrescription(medicalCertificate);
        setMedicalCertificateMode(MEDICAL_CERTIFICATE_MODE.NONE);
      } catch (error) { }
    },
    [resetSearchBar, addMedicalCertificateToPrescription]
  );

  const onConfirmEditMedicalCertificate = useCallback(
    async (medicalCertificate: MedicalCertificateDTO) => {
      try {
        resetSearchBar();

        await updatePrescriptionMedicalCertificate(medicalCertificate);
        setMedicalCertificateMode(MEDICAL_CERTIFICATE_MODE.NONE);
      } catch (error) { }
    },
    [resetSearchBar, updatePrescriptionMedicalCertificate]
  );

  const onChangeSearchByActivePrinciple = useCallback(
    (event: React.ChangeEvent<HTMLInputElement>) => {
      if (!prescription) return;

      setShowByActivePrinciple(prescription.idPrescricao, event.target.checked);
    },
    [prescription, setShowByActivePrinciple]
  );

  const SearchByActivePrincipleToggle = useMemo(() => {
    if (!showSearch || allItems.allMedicines.length === 0) return null;

    return (
      <Box mt={{ base: '5px', md: 0 }} minWidth={{ base: '100%', md: '280px' }}>
        <FormControl display="flex" alignItems="center">
          <Switch
            id={'search-for-dcb'}
            mr={'5px'}
            isChecked={showByActivePrinciple}
            onChange={onChangeSearchByActivePrinciple}
          />
          <FormLabel htmlFor="search-for-dcb" mb="0" fontSize={'12px'} color={'typography.main'} fontWeight={600}>
            Emitir receita por princípio ativo (DCB)
          </FormLabel>
        </FormControl>
      </Box>
    );
  }, [showSearch, allItems.allMedicines.length, showByActivePrinciple, onChangeSearchByActivePrinciple]);

  const addPrescriptionObservation = () => {
    cleanEditionAndAdition();
    setPrescriptionObservationMode(PRESCRIPTION_OBSERVATION_MODE.EDITION);
  };

  const onCancelEditPrescriptionObservation = () => {
    setPrescriptionObservationMode(PRESCRIPTION_OBSERVATION_MODE.NONE);
  };

  // O erro já foi avisado por toast dentro do store; aqui só não deixamos a rejeição escapar,
  // para o modo de edição fechar como fechava antes de `onUpdatePrescriptionObservation` relançar.
  const onConfirmEditPrescriptionObservation = async (value: string) => {
    try {
      await onUpdatePrescriptionObservation(value || null);
    } catch (e) {
      logger.error('onConfirmEditPrescriptionObservation: failed updating observation', e);
    }
    setPrescriptionObservationMode(PRESCRIPTION_OBSERVATION_MODE.NONE);
  };

  const onConfirmRemovePrescriptionObservation = async () => {
    try {
      await onUpdatePrescriptionObservation(null);
    } catch (e) {
      logger.error('onConfirmRemovePrescriptionObservation: failed removing observation', e);
    }
    setPrescriptionObservationMode(PRESCRIPTION_OBSERVATION_MODE.NONE);
  };

  const onEditPrescriptionObservation = () => {
    cleanEditionAndAdition();
    setPrescriptionObservationMode(PRESCRIPTION_OBSERVATION_MODE.EDITION);
  };

  const onCancelMedicalReport = useCallback(() => {
    resetSearchBar();
    setMedicalReportMode(MEDICAL_REPORT_MODE.NONE);
    setEditingMedicalReport(null);
  }, [resetSearchBar]);

  const onAddMedicalReport = useCallback(() => {
    cleanEditionAndAdition();
    setMedicalReportMode(MEDICAL_REPORT_MODE.ADDITION);
  }, [cleanEditionAndAdition]);

  const onConfirmAddMedicalReport = useCallback(
    async (medicalReport: MedicalReportDTO) => {
      try {
        resetSearchBar();
        await addMedicalReportToPrescription(medicalReport);
        setMedicalReportMode(MEDICAL_REPORT_MODE.NONE);
        setEditingMedicalReport(null);
      } catch (error) { }
    },
    [resetSearchBar, addMedicalReportToPrescription]
  );

  const onRemoveMedicalReport = useCallback(
    async (medicalReportId: number) => {
      try {
        const report = prescription?.Relatorios?.find(
          (r: MedicalReport) => r.idPrescricaoLaudoMedico === medicalReportId
        );
        amplitudeModule.removePrescriptionItemClicked({
          partner_id: prescriberConfiguration?.idParceiro ?? 0,
          partner_name: prescriberConfiguration?.Nome ?? '',
          establishment_name: prescription?.Estabelecimento?.Nome ?? '',
          medical_report: [
            {
              id: medicalReportId,
              name: report?.Titulo ?? '',
            },
          ],
        });
        await removeMedicalReportFromPrescription(medicalReportId);
      } catch (error) { }
    },
    [removeMedicalReportFromPrescription, prescription, prescriberConfiguration]
  );

  const onEditMedicalReport = useCallback(
    (medicalReport: any) => {
      amplitudeModule.editPrescriptionItemClicked({
        partner_id: prescriberConfiguration?.idParceiro ?? 0,
        partner_name: prescriberConfiguration?.Nome ?? '',
        establishment_name: prescription?.Estabelecimento?.Nome ?? '',
        medical_report: [
          {
            id: medicalReport.idPrescricaoLaudoMedico ?? 0,
            name: medicalReport.Titulo ?? '',
          },
        ],
      });

      cleanEditionAndAdition();
      changeSearchBarType(SEARCH_TYPE.MEDICAL_REPORT);
      setMedicalReportMode(MEDICAL_REPORT_MODE.EDITION);
      setEditingMedicalReport(medicalReport);
    },
    [cleanEditionAndAdition, changeSearchBarType, prescriberConfiguration, prescription?.Estabelecimento?.Nome]
  );

  const onUpdateGeneralDataExam = useCallback(
    async (structuredIcd: Icd | null, observation: string) => {
      if (!prescription) return;

      const payload = {
        Instrucoes: observation,
        ...(structuredIcd && {
          CID10Estruturado: {
            Codigo: structuredIcd.subcategory,
            idCID10: structuredIcd.id,
            Descricao: structuredIcd.description,
          },
        }),
      };

      try {
        await ExamService.updateGeneralDataExam(prescription.idPrescricao, payload);
      } catch (error) { }
    },
    [prescription]
  );

  const onConfirmEditExam = useCallback(
    async (exam: Exam) => {
      try {
        await updatePrescriptionExam(exam);
      } catch (error) { }
    },
    [updatePrescriptionExam]
  );

  const onConfirmEditMedicalReport = useCallback(
    async (medicalReport: MedicalReportDTO) => {
      try {
        resetSearchBar();
        await updatePrescriptionMedicalReport(medicalReport);
        setMedicalReportMode(MEDICAL_REPORT_MODE.NONE);
      } catch (error) { }
    },
    [updatePrescriptionMedicalReport, resetSearchBar]
  );

  const onCancelMedicalReferral = useCallback(() => {
    cleanEditionAndAdition();
    resetSearchBar();
  }, [resetSearchBar, cleanEditionAndAdition]);

  const onAddMedicalReferral = useCallback(() => {
    Tracker.trackEvent({
      event: 'Include Medical Referral Letter',
    });

    setMedicalReferralMode(MEDICAL_REFERRAL_MODE.ADDITION);
  }, []);

  const onConfirmAddMedicalReferral = useCallback(
    async (referral: MedicalReferral) => {
      try {
        resetSearchBar();
        await addMedicalReferralToPrescription(referral);
        setMedicalReferralMode(MEDICAL_REFERRAL_MODE.NONE);
      } catch (error) { }
    },
    [addMedicalReferralToPrescription, resetSearchBar]
  );

  const onRemoveMedicalReferral = useCallback(
    async (medicalReferralId: number) => {
      try {
        const referral = prescription?.Encaminhamentos?.find(
          (r: MedicalReferral) => r.idEncaminhamentoPrescricao === medicalReferralId
        );
        amplitudeModule.removePrescriptionItemClicked({
          partner_id: prescriberConfiguration?.idParceiro ?? 0,
          partner_name: prescriberConfiguration?.Nome ?? '',
          establishment_name: prescription?.Estabelecimento?.Nome ?? '',
          referral: [
            {
              id: medicalReferralId,
              name: referral?.Especialidade?.Nome ?? '',
              is_voice_prescribed: referral?.AdicionadoPorIA ?? false,
            },
          ],
        });
        await removeMedicalReferralFromPrescription(medicalReferralId);
      } catch (error) { }
    },
    [removeMedicalReferralFromPrescription, prescription, prescriberConfiguration]
  );

  const onEditMedicalReferral = useCallback(
    (referral: MedicalReferral) => {
      amplitudeModule.editPrescriptionItemClicked({
        partner_id: prescriberConfiguration?.idParceiro ?? 0,
        partner_name: prescriberConfiguration?.Nome ?? '',
        establishment_name: prescription?.Estabelecimento?.Nome ?? '',
        referral: [
          {
            id: referral.idEncaminhamentoPrescricao,
            name: referral.Especialidade?.Nome ?? '',
            is_voice_prescribed: referral.AdicionadoPorIA ?? false,
          },
        ],
      });

      cleanEditionAndAdition();
      changeSearchBarType(SEARCH_TYPE.MEDICAL_REFERRAL);
      setMedicalReferralMode(MEDICAL_REFERRAL_MODE.EDITION);
      setEditingMedicalReferral(referral);
    },
    [changeSearchBarType, cleanEditionAndAdition, prescriberConfiguration, prescription?.Estabelecimento?.Nome]
  );

  const onConfirmEditMedicalReferral = useCallback(
    async (referral: MedicalReferral) => {
      try {
        resetSearchBar();
        await updateMedicalReferral(referral);
        setMedicalReferralMode(MEDICAL_REFERRAL_MODE.NONE);
      } catch (error) { }
    },
    [updateMedicalReferral, resetSearchBar]
  );

  const canAddMedicalReferral = useMemo(() => {
    if (!prescriberConfiguration) return false;

    if (
      !allPermissionsFalse &&
      !hasCRMorCRO &&
      doctorPermissions?.Referral !== undefined &&
      doctorPermissions?.Referral !== null &&
      typeof doctorPermissions?.Referral === 'boolean'
    ) {
      return doctorPermissions?.Referral;
    }

    return get(prescriberConfiguration, 'Features.enableReferral', false);
  }, [prescriberConfiguration, doctorPermissions?.Referral, allPermissionsFalse, hasCRMorCRO]);

  const canUseLME = useMemo(() => {
    if (!prescriberConfiguration) return true;
    return !get(prescriberConfiguration, 'Features.disableLME', false);
  }, [prescriberConfiguration]);

  const canUseParanaSemDor = useMemo(() => {
    if (!prescriberConfiguration) return false;
    return get(prescriberConfiguration, 'Features.enableProgramParanaSemDor', false);
  }, [prescriberConfiguration]);

  const canAddMedicalReport = useMemo(() => {
    if (!prescriberConfiguration) return false;

    if (
      !allPermissionsFalse &&
      !hasCRMorCRO &&
      doctorPermissions?.MedicalReport !== undefined &&
      doctorPermissions?.MedicalReport !== null &&
      typeof doctorPermissions?.MedicalReport === 'boolean'
    ) {
      return doctorPermissions?.MedicalReport;
    }

    return get(prescriberConfiguration, 'Features.enableMedicalReport', false);
  }, [prescriberConfiguration, doctorPermissions?.MedicalReport, allPermissionsFalse, hasCRMorCRO]);

  const handleSetLaterality = (value: boolean, isLateralityFilled: boolean) => {
    setIsLateralityFilled(isLateralityFilled);
    setNeedsLaterality(value);
  };

  const shouldLimitPosologyField = useMemo(() => {
    if (!prescriberConfiguration) return !prescriberConfiguration;

    return get(prescriberConfiguration, 'Features.enablePosologyLimit', false);
  }, [prescriberConfiguration]);

  const canShowMedicalPrescription = isProfessionalIssuerDoctor(prescriber?.Conselho || '');

  const canSearchAllIcdsOnLME = get(prescriberConfiguration, 'Features.canSearchAllIcdsOnLme', false);
  const enableFractionalUnits = get(prescriberConfiguration, 'Features.enableFractionedQuantity', false);
  const showMedicamentPackingWhenContinuous = get(
    prescriberConfiguration,
    'Features.showMedicamentPackingWhenContinuous',
    false
  );

  const enablePreSelectFractionalUnits = get(prescriberConfiguration, 'Features.enablePreSelectFractionalUnits', false);
  const enableSecondaryIcdsForMedicalStatement = get(
    prescriberConfiguration,
    'Features.enableSecondaryIcdsForMedicalStatement',
    false
  );

  const enableRequiredICDMedicalStatement = get(
    prescriberConfiguration,
    'Features.enableRequiredICDMedicalStatement',
    false
  );

  const enablePreCheckConsentMedicalStatement = get(
    prescriberConfiguration,
    'Features.enablePreCheckConsentMedicalStatement',
    false
  );

  const patientName = get(prescription, 'Paciente.Nome', '');
  const patientSocialName = get(prescription, 'Paciente.NomeSocial', '');

  const disableDeclarationOfAttendance = get(prescriberConfiguration, 'Features.disableDeclarationOfAttendance', false);
  const enableCompanionMedicalCertificate = get(
    prescriberConfiguration,
    'Features.enableCompanionMedicalCertificate',
    false
  );

  const enableUserGuiding = get(prescriberConfiguration, 'Features.enableUserGuiding', false);

  const enableICDPreFillingOnMedicalStatement = get(
    prescriberConfiguration,
    'Features.enableICDPreFillingOnMedicalStatement',
    false
  );
  const enableConcomitanceMedicines = prescriberConfiguration?.Features?.enableConcomitanceMedicines;

  const enableReferralType = get(prescriberConfiguration, 'Features.enableReferralType', false);

  const isEditingNonSearchableDocument = useMemo(() => {
    return (
      medicalReferralMode === MEDICAL_REFERRAL_MODE.EDITION ||
      medicalReportMode === MEDICAL_REPORT_MODE.EDITION ||
      medicalCertificateMode === MEDICAL_CERTIFICATE_MODE.EDITION
    );
  }, [medicalReferralMode, medicalReportMode, medicalCertificateMode]);

  const isEditingOrAddingDocument = useMemo(() => {
    return (
      !!addingMedicicine ||
      !!addingManualMedicicine ||
      !!editionManualMedicine ||
      prescriptionObservationMode !== PRESCRIPTION_OBSERVATION_MODE.NONE ||
      !!editionMedicine ||
      medicalReferralMode !== MEDICAL_REFERRAL_MODE.NONE ||
      medicalCertificateMode !== MEDICAL_CERTIFICATE_MODE.NONE ||
      prescriptionObservationMode !== PRESCRIPTION_OBSERVATION_MODE.NONE ||
      medicalReportMode !== MEDICAL_REPORT_MODE.NONE
    );
  }, [
    addingManualMedicicine,
    addingMedicicine,
    editionManualMedicine,
    editionMedicine,
    medicalCertificateMode,
    medicalReferralMode,
    medicalReportMode,
    prescriptionObservationMode,
  ]);

  const verifyCanAddExam = (): boolean => {
    if (
      !allPermissionsFalse &&
      !hasCRMorCRO &&
      doctorPermissions?.Exam !== undefined &&
      doctorPermissions?.Exam !== null &&
      typeof doctorPermissions?.Exam === 'boolean'
    ) {
      return doctorPermissions?.Exam;
    }

    if (prescription?.Origem?.BloquearEstabelecimentosParaExames) {
      return false;
    }

    if (prescriberConfiguration) return !!prescriberConfiguration.HabilitarExame;
    return false;
  };

  useEffect(() => {
    // este useEffect roda ao alterar o tipo de busca
    // ao editar um dos documentos abaixo o switch nao deve ser executado

    if (isEditingNonSearchableDocument) return;

    switch (searchType) {
      case SEARCH_TYPE.MEDICAL_CERTIFICATE:
        onClickToAddMedicalCertificate();
        break;
      case SEARCH_TYPE.MEDICAL_REFERRAL:
        onAddMedicalReferral();
        break;
      case SEARCH_TYPE.MEDICAL_REPORT:
        onAddMedicalReport();
        break;
    }
  }, [
    onAddMedicalReferral,
    onAddMedicalReport,
    onClickToAddMedicalCertificate,
    searchType,
    isEditingNonSearchableDocument,
  ]);

  useEffect(() => {
    if (!prescription || !prescriberConfiguration) return;

    if (
      prescription.idPrescricaoStatus === PRESCRIPTION_STATUS.EMITTED &&
      get(prescriberConfiguration, 'Features.showFinishedPrescriptionScreen', true)
    ) {
      history.replace('/prescriptionemitted');
    }
  }, [history, prescriberConfiguration, prescription]);

  const signatureRaffleFlow = useMemo(() => {
    const flow = prescriberConfigurations?.Configuracoes?.signatureRaffleFlow || null;

    if (!flow) return flow;

    return flow;
  }, [prescriberConfigurations]);

  const userClosedTestEvent = useCallback(async () => {
    if (!prescription) return;

    await TrackerEventService.track(prescription.idPrescricao, 'DIALOG INICIAL FECHADO SEM SELECIONAR CD');
  }, [prescription]);

  useEffect(() => {
    if (!prescription?.Prescritor || !prescriberConfiguration) return;

    const userGuiding = get(window, 'userGuiding', null) as any;

    if (userGuiding && prescriberConfiguration.idParceiro && enableUserGuiding) {
      userGuiding.identify(prescription.Prescritor.idPrescritor, {
        name: prescription.Prescritor.Nome,
        email: prescription.Prescritor.Email,
        partner: prescriberConfiguration.idParceiro,
      });
    }
  }, [prescriberConfiguration, prescription?.Prescritor, enableUserGuiding]);

  useEffect(() => {
    const run = async () => {
      if (
        (signatureRaffleFlow === TEST_FLOW.A || signatureRaffleFlow === TEST_FLOW.B) &&
        prescriptionItemsAmount === 0
      ) {
        await openBaseDialog(
          (dialogProps: DialogWrappedComponentProps) => {
            return (
              <InitialCertificateFlow
                testFlow={signatureRaffleFlow}
                onCancel={() => {
                  dialogProps.onClose();
                  userClosedTestEvent();
                }}
                onComplete={dialogProps.onClose}
              />
            );
          },
          {
            width: 'auto',
            height: 'auto',
            onOverlayClickCallback: userClosedTestEvent,
            backgroundColor: 'transparent',
          }
        );
      }
    };

    run();
  }, [signatureRaffleFlow, openBaseDialog, prescriptionItemsAmount, userClosedTestEvent]);

  const setAllConcomitances = useCallback(async () => {
    const partnerId = prescriberConfiguration?.idParceiro;
    const doctorSpecialities = prescription?.Prescritor?.RQEs;
    const prescriberId = prescription?.idPrescritor;

    const age = extractPatientAge(prescription?.Paciente?.Idade);

    let speciality: string[] | undefined;
    const rqes = doctorSpecialities?.map((rqe) => rqe.idRqe).filter((idRqe) => idRqe != null);

    if (rqes?.length === 0 && prescriberId) {
      try {
        const rqeResponse = await fetchDoctorRqes(prescriberId);
        if (rqeResponse?.data?.length > 0) {
          speciality = rqeResponse.data.map((rqe: any) => rqe.idRqe).filter((idRqe: string) => idRqe != null);
        }
      } catch (error) {
        //  comentando por agora até termos outra configuração de log
        //  console.error('Erro ao buscar RQEs do médico:', error);
      }
    } else {
      speciality = rqes as string[] | undefined;
    }

    const medicineUpdatePromises = allItems.allMedicines.map(async (med: any) => {
      try {
        const concomitanceRecords = await MedicineService.getConcomitantMedicines(
          med.IdMedicamento,
          prescription?.idPrescricao || 0,
          partnerId,
          speciality,
          age
        );

        const allProducts = concomitanceRecords?.flatMap((campaign) => campaign.products || []) || [];
        const ConcomitanceMedicines = allProducts
          .flatMap((product) => product.concomitance?.concomitantMedicine || [])
          .filter((medicine): medicine is Medicine => Boolean(medicine));

        const isHighlighted = allProducts.some((product) => product.concomitance?.isHighlighted) || false;

        if (ConcomitanceMedicines.length > 0) {
          return {
            ...med,
            ConcomitanceMedicines,
            isHighlighted,
          };
        }
      } catch (error) {
        logger.error(`Falha ao buscar concomitâncias para o medicamento ${med.IdMedicamento}:`, error);
      }
      return med;
    });

    const updatedMedicines = await Promise.all(medicineUpdatePromises);

    const newAllItems = {
      ...allItems,
      allMedicines: updatedMedicines,
    };

    setAllItemsToSend(newAllItems);
  }, [
    allItems,
    prescription?.idPrescricao,
    prescription?.Paciente?.Idade,
    prescription?.Prescritor?.RQEs,
    prescription?.idPrescritor,
    prescriberConfiguration?.idParceiro,
  ]);

  useEffect(() => {
    if (enableConcomitanceMedicines) setAllConcomitances();
  }, [setAllConcomitances, enableConcomitanceMedicines]);

  return (
    <PrescriptionLayout
      userSignUpStatus={prescriberConfiguration?.EasySignupStatus || null}
      continueButtonLabel={'Continuar'}
      continueDisabled={prescriptionItemsAmount === 0}
      loading={loadingInteractions || documentsLoading}
      onContinue={onContinueFooter}
      onCancel={onCancelFooter}
      showMyCentral={true}
      showMenu={true}
      footerLeftAction={<SaveAsModelButton />}
    >
      <div
        style={{
          width: '100%',
          position: 'relative',
        }}
      >
        <Search
          onSelectMedicine={onSelectMedicine}
          onSelectExam={onSelectExam}
          canAddExam={verifyCanAddExam()}
          canAddMedicalCertificate={(() => {
            if (
              !allPermissionsFalse &&
              !hasCRMorCRO &&
              doctorPermissions?.MedicalStatement !== undefined &&
              doctorPermissions?.MedicalStatement !== null &&
              typeof doctorPermissions?.MedicalStatement === 'boolean'
            ) {
              return doctorPermissions?.MedicalStatement;
            }
            return prescriberConfiguration ? !!prescriberConfiguration.HabilitarAtestado : false;
          })()}
          onSelectManualMedicine={onSelectManualMedicine}
          prescriptionId={prescription ? prescription.idPrescricao : null}
          prescriberId={get(prescription, 'Prescritor.idPrescritor', null)}
          doctorId={get(prescription, 'Prescritor.idPrescritor', null)}
          benefit={benefit}
          canAddMedicalReferral={canAddMedicalReferral}
          onClearEditionAndAdition={cleanEditionAndAdition}
          canFilterRename={!!prescription?.PodeFiltrarRename}
          canFilterRemume={!!prescription?.PodeFiltrarRemume}
          canAddMedicalReport={canAddMedicalReport}
          searchInputRef={searchInputRef}
          onSelectFormula={onSelectFormula}
          doctorPermissions={doctorPermissions || {}}
          allPermissionsFalse={allPermissionsFalse}
          hasCRMorCRO={hasCRMorCRO}
          searchBarRightAction={<OpenModelsButton />}
        />

        <Modal
          isOpen={isEditingOrAddingDocument}
          onClose={() => {
            cleanEditionAndAdition();
            resetSearchBar();
          }}
          isCentered={!isMobile}
          motionPreset="slideInLeft"
        >
          <ModalOverlay
            bg="rgba(255, 255, 255, 0.59)"
            backdropFilter={'blur(2px)'}
            top={{ base: HEADER_HEIGHT_MOBILE, sm: HEADER_HEIGHT }}
          />
          <ModalContent
            bg={'white'}
            p={'0px'}
            m={0}
            maxW={{ base: '100%', sm: sideBarWidth === SIDEBAR_WIDTH.EXPANDED ? '80%' : '90%' }}
            w={'100%'}
            minH={{ base: APP_VIEWPORT_HEIGHT, sm: 'auto' }}
            maxH={{ base: APP_VIEWPORT_HEIGHT, sm: appViewportHeight(0.9) }}
            overflowY={'auto'}
            boxShadow={{ base: 'none', sm: '0px 0px 20px 7px rgba(0,0,0,0.25)' }}
            borderRadius={{ base: '0', sm: '13px' }}
          >
            {addingMedicicine && (
              <EditMedicine
                mode={'adition'}
                medicine={addingMedicicine.medicine}
                index={addingMedicicine.index}
                onConfirmEdition={confirmAddMedicine}
                onCancelEdition={cancelAddMedicine}
                canUseLME={canUseLME}
                canUseParanaSemDor={canUseParanaSemDor}
                canSearchAllIcdsOnLME={canSearchAllIcdsOnLME}
                isFromConcomitanceSidebar={addingMedicicine.isFromConcomitanceSidebar}
                enableFractionalUnits={enableFractionalUnits}
                enablePreSelectFractionalUnits={enablePreSelectFractionalUnits}
                shouldLimitPosologyField={shouldLimitPosologyField}
                showMedicamentPackingWhenContinuous={showMedicamentPackingWhenContinuous}
                allConcomitanceItens={
                  enableConcomitanceMedicines && allItemsToSend.allMedicines ? allItemsToSend.allMedicines : []
                }
                onOpenPspSidebar={handleOpenPspSidebar}
                onOpenPbmSidebar={handleOpenPbmSidebar}
                onOpenWhatsappPbmSidebar={handleOpenWhatsappPbmSidebar}
              />
            )}

            {addingManualMedicicine && (
              <EditManualMedicine
                mode={'adition'}
                medicine={addingManualMedicicine.medicine}
                index={addingManualMedicicine.index}
                onConfirmEdition={onConfirmAddManualMedicine}
                onCancelEdition={onCancelAddManualMedicine}
                shouldLimitPosologyField={shouldLimitPosologyField}
              />
            )}

            {editionManualMedicine && (
              <EditManualMedicine
                mode={'edition'}
                medicine={editionManualMedicine.medicine}
                index={editionManualMedicine.index}
                onConfirmEdition={onConfirmEditManualMedicine}
                onCancelEdition={onCancelEditManualMedicine}
                shouldLimitPosologyField={shouldLimitPosologyField}
              />
            )}

            {prescriptionObservationMode !== PRESCRIPTION_OBSERVATION_MODE.NONE && (
              <EditPrescriptionObservation
                onCancel={onCancelEditPrescriptionObservation}
                onConfirm={onConfirmEditPrescriptionObservation}
                observation={prescriptionObservation}
              />
            )}

            {editionMedicine && (
              <EditMedicine
                mode={'edition'}
                medicine={editionMedicine.medicine}
                index={editionMedicine.index}
                onConfirmEdition={confirmEditMedicine}
                onCancelEdition={onCancelEditMedicine}
                canUseLME={canUseLME}
                canUseParanaSemDor={canUseParanaSemDor}
                canSearchAllIcdsOnLME={canSearchAllIcdsOnLME}
                enableFractionalUnits={enableFractionalUnits}
                enablePreSelectFractionalUnits={enablePreSelectFractionalUnits}
                shouldLimitPosologyField={shouldLimitPosologyField}
                showMedicamentPackingWhenContinuous={showMedicamentPackingWhenContinuous}
              />
            )}

            {medicalReferralMode !== MEDICAL_REFERRAL_MODE.NONE && (
              <EditMedicalReferral
                mode={medicalReferralMode === MEDICAL_REFERRAL_MODE.ADDITION ? 'addition' : 'edition'}
                medicalReferral={editingMedicalReferral || null}
                prescription={prescription}
                onCancel={onCancelMedicalReferral}
                onConfirm={onConfirmAddMedicalReferral}
                onEdit={onConfirmEditMedicalReferral}
                enableReferralType={enableReferralType}
                structuredCid={incommingStructuredCid}
              />
            )}

            {medicalCertificateMode !== MEDICAL_CERTIFICATE_MODE.NONE && (
              <EditMedicalCertificate
                medicalCertificate={editingMedicalCertificate || null}
                onCancel={onCancelEditMedicalCertificate}
                mode={medicalCertificateMode === MEDICAL_CERTIFICATE_MODE.ADDITION ? 'addition' : 'edition'}
                onConfirm={onConfirmAddMedicalCertificate}
                onEdit={onConfirmEditMedicalCertificate}
                professionalIssuer={prescriber?.Conselho || ''}
                patientName={patientSocialName || patientName}
                disableDeclarationOfAttendance={disableDeclarationOfAttendance}
                enableICDPreFillingOnMedicalStatement={enableICDPreFillingOnMedicalStatement}
                enableCompanionMedicalCertificate={enableCompanionMedicalCertificate}
                enableSecondaryIcds={enableSecondaryIcdsForMedicalStatement}
                enableRequiredICD={enableRequiredICDMedicalStatement}
                enablePreCheckConsent={enablePreCheckConsentMedicalStatement}
                structuredCid={incommingStructuredCid}
              />
            )}

            {medicalReportMode !== MEDICAL_REPORT_MODE.NONE && (
              <EditMedicalReport
                mode={medicalReportMode === MEDICAL_REPORT_MODE.ADDITION ? 'addition' : 'edition'}
                medicalReport={editingMedicalReport || null}
                onCancel={onCancelMedicalReport}
                onConfirm={onConfirmAddMedicalReport}
                onEdit={onConfirmEditMedicalReport}
              />
            )}
          </ModalContent>
        </Modal>

        {searchType === SEARCH_TYPE.EXAMS ? (
          <Box padding={'0 16px'}>
            <PrescriptionExamContent
              exams={exams || []}
              onDeleteExam={onDeleteExam}
              onUpdateExam={onConfirmEditExam}
              onUpdateGeneralDataExam={onUpdateGeneralDataExam}
              enableTussCodeOnInterface={enableTussCodeOnInterface}
              registerExamConfirmCallback={registerExamConfirmCallback}
              needsLaterality={needsLaterality}
              handleSetLaterality={handleSetLaterality}
              structuredCid={incommingStructuredCid}
            />
          </Box>
        ) : searchType === SEARCH_TYPE.FORMULA ? (
          <Box padding={'0 16px'}>
            <PrescriptionFormulaContent
              prescriptionId={prescription?.idPrescricao || 0}
              doctorId={prescriber?.idPrescritor || 0}
              isFormulaFormDialogOpen={isFormulaFormDialogOpen}
              setIsFormulaFormDialogOpen={setIsFormulaFormDialogOpen}
              selectedFormula={selectedFormula}
              setSelectedFormula={setSelectedFormula}
            />

            <PrescriptionContent
              prescriptionObservation={prescriptionObservation}
              onEditPrescriptionObservation={onEditPrescriptionObservation}
              onConfirmRemovePrescriptionObservation={onConfirmRemovePrescriptionObservation}
              addPrescriptionObservation={addPrescriptionObservation}
              exams={exams}
              formulas={formulas}
              medicalReferrals={medicalReferrals}
              medicalCertificates={medicalCertificates}
              medicalReports={medicalReports}
              allItems={enableConcomitanceMedicines ? allItemsToSend : allItems}
              onRemoveManualMedicine={onRemoveManualMedicine}
              onEditManualMedicine={onEditManualMedicine}
              onRemoveMedicine={onRemoveMedicine}
              medicineDrugInteractions={medicineDrugInteractions}
              onEditMedicine={onEditMedicine}
              onClickInteraction={onClickInteraction}
              onDeleteExam={onDeleteExam}
              onUpdateExam={onConfirmEditExam}
              onEditExam={onEditExam}
              enableTussCodeOnInterface={enableTussCodeOnInterface}
              onEditMedicalCertificate={onEditMedicalCertificate}
              onDeleteMedicalCertificate={onDeleteMedicalCertificate}
              onRemoveMedicalReferral={onRemoveMedicalReferral}
              onEditMedicalReferral={onEditMedicalReferral}
              onEditMedicalReport={onEditMedicalReport}
              onRemoveMedicalReport={onRemoveMedicalReport}
              onIncludeConcomitanceMedicine={onIncludeConcomitanceMedicine}
              setSelectedFormula={setSelectedFormula}
              selectedFormula={selectedFormula}
              isFormulaFormDialogOpen={isFormulaFormDialogOpen}
              setIsFormulaFormDialogOpen={setIsFormulaFormDialogOpen}
              isFormulaContent={true}
            />
          </Box>
        ) : (
          <Box padding={'0 16px'}>
            {!isEmpty(allItems.allMedicines) && (
              <Box
                mt={4}
                display={'flex'}
                flexDirection={{ base: 'column', md: 'row' }}
                justifyContent={{ base: 'flex-start', md: 'center' }}
                alignItems={{ base: 'flex-start', md: 'center' }}
              >
                <Box>
                  <Anchor anchorName={ANCHORS.PRESCRIPTION_MEDICINES} position={ANCHOR_POS.RIGHT}>
                    <Text fontSize={'16px'} fontWeight={500} whiteSpace="nowrap" color={'typography.main'}>
                      Receita {canShowMedicalPrescription ? 'médica' : ''}
                    </Text>
                  </Anchor>
                </Box>

                {!isMobile && <Divider m={'0 8px'} />}

                {SearchByActivePrincipleToggle}
              </Box>
            )}

            <PrescriptionContent
              prescriptionObservation={prescriptionObservation}
              onEditPrescriptionObservation={onEditPrescriptionObservation}
              onConfirmRemovePrescriptionObservation={onConfirmRemovePrescriptionObservation}
              addPrescriptionObservation={addPrescriptionObservation}
              exams={exams}
              formulas={formulas}
              medicalReferrals={medicalReferrals}
              medicalCertificates={medicalCertificates}
              medicalReports={medicalReports}
              allItems={enableConcomitanceMedicines ? allItemsToSend : allItems}
              onRemoveManualMedicine={onRemoveManualMedicine}
              onEditManualMedicine={onEditManualMedicine}
              onRemoveMedicine={onRemoveMedicine}
              medicineDrugInteractions={medicineDrugInteractions}
              onEditMedicine={onEditMedicine}
              onClickInteraction={onClickInteraction}
              onDeleteExam={onDeleteExam}
              onUpdateExam={onConfirmEditExam}
              onEditExam={onEditExam}
              enableTussCodeOnInterface={enableTussCodeOnInterface}
              onEditMedicalCertificate={onEditMedicalCertificate}
              onDeleteMedicalCertificate={onDeleteMedicalCertificate}
              onRemoveMedicalReferral={onRemoveMedicalReferral}
              onEditMedicalReferral={onEditMedicalReferral}
              onEditMedicalReport={onEditMedicalReport}
              onRemoveMedicalReport={onRemoveMedicalReport}
              onIncludeConcomitanceMedicine={onIncludeConcomitanceMedicine}
              setSelectedFormula={setSelectedFormula}
              selectedFormula={selectedFormula}
              isFormulaFormDialogOpen={isFormulaFormDialogOpen}
              setIsFormulaFormDialogOpen={setIsFormulaFormDialogOpen}
            />
          </Box>
        )}
      </div>

      {shouldShowAudioModal && (
        <AudioModal
          prescriptionId={prescription?.idPrescricao}
          onShowIncompleteModal={() => setShowIncompleteModal(true)}
        />
      )}

      <IncompletePrescriptionsModal isOpen={showIncompleteModal} onClose={() => setShowIncompleteModal(false)} />

      <PspModal
        isOpen={showPspModal}
        onClose={handlePspModalCancel}
        onConfirm={handlePspModalConfirm}
        prescription={
          prescription
            ? {
              idPrescricao: prescription.idPrescricao,
              CodigoValidacao: prescription.CodigoValidacao,
              Paciente: prescription.Paciente,
              Prescritor: prescription.Prescritor,
              idPrescritor: prescription.idPrescritor,
            }
            : undefined
        }
        prescriberConfiguration={prescriberConfiguration || undefined}
        medicineData={pendingPspMedicineData?.medicineData}
      />
      {prescription && pendingPspMedicineData?.programData && (
        <PspSidebar
          isOpen={showPspSidebar}
          onClose={handlePspSidebarCancel}
          patientEmail={prescription.Paciente?.Emails?.[0] || ''}
          patientPhone={prescription.Paciente?.Telefones?.[0] || ''}
          programName={(() => {
            return pendingPspMedicineData?.programData?.program?.name || '';
          })()}
          optoutUrl={pendingPspMedicineData?.programData?.program?.optoutUrl || ''}
          partnerProgramUrl={pendingPspMedicineData?.programData?.program?.partnerProgramUrl || ''}
          onConfirm={handlePspSidebarConfirm}
          onCancel={handlePspSidebarCancel}
          medicineData={pendingPspMedicineData?.medicineData}
          prescription={{
            idPrescricao: prescription.idPrescricao,
            Paciente: prescription.Paciente,
            Prescritor: prescription.Prescritor,
            idPrescritor: prescription.idPrescritor,
          }}
          partnerId={prescriberConfiguration?.idParceiro}
          programId={pendingPspMedicineData?.programData?.program?.id}
          eanCode={pendingPspMedicineData?.programData?.program?.medicines?.[0]?.eanCode}
          programData={pendingPspMedicineData?.programData}
          initialStep={pspSidebarInitialStep}
        />
      )}
      <PbmModal
        isOpen={showPbmModal}
        onClose={handlePbmModalCancel}
        onConfirm={handlePbmModalConfirm}
        prescription={
          prescription
            ? {
              idPrescricao: prescription.idPrescricao,
              CodigoValidacao: prescription.CodigoValidacao,
              Paciente: prescription.Paciente,
              Prescritor: prescription.Prescritor,
              idPrescritor: prescription.idPrescritor,
            }
            : undefined
        }
        prescriberConfiguration={prescriberConfiguration || undefined}
        medicineData={pendingPbmMedicineData?.medicineData}
      />
      {prescription && pendingPbmMedicineData && (pendingPbmMedicineData.programData || pbmSidebarInitialStep === 'error') && (
        <PbmSidebar
          isOpen={showPbmSidebar}
          onClose={handlePbmSidebarCancel}
          patientEmail={prescription.Paciente?.Emails?.[0] || ''}
          patientPhone={prescription.Paciente?.Telefones?.[0] || ''}
          programName={
            pendingPbmMedicineData?.programData?.program?.name ||
            getPbmBenefit(pendingPbmMedicineData.medicineData.Beneficios)?.Nome ||
            ''
          }
          optoutUrl={pendingPbmMedicineData?.programData?.program?.optoutUrl || ''}
          partnerProgramUrl={pendingPbmMedicineData?.programData?.program?.partnerProgramUrl || ''}
          controllerName={pendingPbmMedicineData?.programData?.program?.partner || ''}
          onConfirm={handlePbmSidebarConfirm}
          onCancel={handlePbmSidebarCleanup}
          medicineData={pendingPbmMedicineData.medicineData}
          prescription={{
            idPrescricao: prescription.idPrescricao,
            Paciente: prescription.Paciente,
            Prescritor: prescription.Prescritor,
            idPrescritor: prescription.idPrescritor,
            CodigoValidacao: prescription.CodigoValidacao,
          }}
          partnerId={prescriberConfiguration?.idParceiro}
          programId={pendingPbmMedicineData?.programData?.program?.id}
          eanCode={pendingPbmMedicineData?.programData?.program?.medicines?.[0]?.eanCode}
          programData={pendingPbmMedicineData?.programData}
          initialStep={pbmSidebarInitialStep}
        />
      )}
      <PbmModal
        isOpen={showWhatsappPbmModal}
        onClose={handleWhatsappPbmModalCancel}
        onConfirm={handleWhatsappPbmModalConfirm}
        prescription={
          prescription
            ? {
                idPrescricao: prescription.idPrescricao,
                CodigoValidacao: prescription.CodigoValidacao,
                Paciente: prescription.Paciente,
                Prescritor: prescription.Prescritor,
                idPrescritor: prescription.idPrescritor,
              }
            : undefined
        }
        prescriberConfiguration={prescriberConfiguration || undefined}
        medicineData={pendingWhatsappPbmMedicine ?? undefined}
        programType={WHATSAPP_PBM_PROGRAM_TYPE}
      />
      {prescription && pendingWhatsappPbmMedicine && (
        <WhatsappPbmSidebar
          isOpen={showWhatsappPbmSidebar}
          onClose={handleWhatsappPbmSidebarClose}
          medicineData={pendingWhatsappPbmMedicine}
          prescription={{
            idPrescricao: prescription.idPrescricao,
            CodigoValidacao: prescription.CodigoValidacao,
            Paciente: prescription.Paciente,
            Prescritor: prescription.Prescritor,
            idPrescritor: prescription.idPrescritor,
          }}
          partnerId={prescriberConfiguration?.idParceiro}
        />
      )}
    </PrescriptionLayout>
  );
}

export default Prescription;
